import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Button, Badge, Tab, Tabs, Modal, Form, Alert, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLocation } from 'react-router-dom';
import HelpPane from './HelpPane';
import FeatureNotice from './FeatureNotice';
import { countries, getStatesForCountry, getOrgAddressDefaults, matchIndianState } from '../utils/countriesStates';
import GstinLookupInput, { GSTIN_PATTERN } from './GstinLookupInput';
import { loadOrgAddressDefaults } from '../utils/orgAddressDefaults';
import ProcurementSurveysTab from './ProcurementSurveysTab';
import PurchaseInvoices from './PurchaseInvoices';
import LowStock from './LowStock';
import ModuleAlerts from './ModuleAlerts';
import NonGstPurchases from './NonGstPurchases';
import PurchaseReports from './PurchaseReports';
import { hasAnyRole } from '../utils/permissionUtils';
import OrgPrintChrome from './OrgPrintChrome';
import PurchaseOrderPrint, { poVendors } from './PurchaseOrderDocument';
import WhatsAppShareModal from './WhatsAppShareModal';
import useSectionTab from '../hooks/useSectionTab';
import WebAddressInput, { checkWebAddress } from './WebAddressInput';
import { useVendorRates } from '../hooks/useLastRates';
import LastRateHint from './LastRateHint';
import { localISODate, todayISO } from '../utils/dates';
import HistoryButton from './HistoryButton';

import DateInput from './DateInput';
import PhoneNumberInput from './PhoneNumberInput';
import { formatDate } from '../utils/dateFormat';
/* Purchase module currently shares leftover Inventory (stock/GRN/GIN) helpers.
   Suppress unused-var noise until that dead code is fully split out. */
/* eslint-disable no-unused-vars */
// Section keys, in tab order; the first is where a first visit opens
const PURCHASE_SECTIONS = ['vendor-types', 'vendors', 'procurement-surveys', 'purchase-orders', 'weigh-bridge-receipts', 'purchase-invoices', 'non-gst-purchases', 'reports'];

function InventoryManagement() {
  const { user } = useAuth();
  // Purchase reports show what each vendor and item costs: the Org Admin and the procurement and finance managers
  const canSeeReports = hasAnyRole(user, ['ORG_ADMIN', 'PROC_MGR', 'FIN_MGR']);
  const location = useLocation();
  const [loading, setLoading] = useState(true);

  // The open section is in the address (/purchase/<section>)
  const [activeTab, setActiveTab] = useSectionTab('/purchase', PURCHASE_SECTIONS);
  const [organizationCurrency, setOrganizationCurrency] = useState('USD'); // Default to USD
  const [orgAddressDefaults, setOrgAddressDefaults] = useState({ country: '', state: '', countryCode: '' });
  
  // Warehouse state (for dropdowns only, not for CRUD)
  const [warehouses, setWarehouses] = useState([]);
  
  // Stock levels state
  const [stockLevels, setStockLevels] = useState([]);
  const [stockFilterWarehouse, setStockFilterWarehouse] = useState('ALL');
  const [showStockTransactions, setShowStockTransactions] = useState(false);
  const [stockTransactions, setStockTransactions] = useState([]);
  const [selectedStockItem, setSelectedStockItem] = useState(null);

  // GRN state
  const [grns, setGrns] = useState([]);
  const [showGRNModal, setShowGRNModal] = useState(false);
  const [selectedGRN, setSelectedGRN] = useState(null);
  const [items, setItems] = useState([]);
  const [organizationMaterials, setOrganizationMaterials] = useState([]);
  const [uoms, setUoms] = useState([]);
  const [grnFormData, setGrnFormData] = useState({
    grnNumber: '',
    grnDate: todayISO(),
    warehouseId: '',
    purchaseOrderId: '',
    remarks: '',
    status: 'DRAFT',
    lineItems: []
  });
  const [grnLineItems, setGrnLineItems] = useState([{ itemId: '', vendorId: '', unitOfMeasurementId: '', receivedQuantity: '', unitPrice: '', lineTotal: '', weighBridgeReceiptNo: '', weighBridgeReceiptId: '', vendorLotNumber: '', internalLotNumber: '', orderedQuantity: '', receivedQuantityTotal: '', pendingQuantity: '', uomSymbol: '', isFromPO: false, additionalCosts: '', allocatedServiceCost: 0, landedCost: 0, landedCostPerUnit: 0 }]);
  const [grnServiceItems, setGrnServiceItems] = useState([]); // Service items from PO
  const [unusedWeighBridgeReceipts, setUnusedWeighBridgeReceipts] = useState([]);
  
  // Print view states
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [printData, setPrintData] = useState(null);
  const [printType, setPrintType] = useState(null); // 'GRN', 'WBR', ...
  const [printingPO, setPrintingPO] = useState(null); // the purchase order document, one vendor's copy at a time
  const [sendingPO, setSendingPO] = useState(null); // { po, vendor }: sending a PO to a vendor on WhatsApp or by email
  
  // Help pane state
  const [showHelpPane, setShowHelpPane] = useState(false);
  const [helpContent, setHelpContent] = useState(null);

  // GIN state
  const [gins, setGins] = useState([]);
  const [showGINModal, setShowGINModal] = useState(false);
  const [selectedGIN, setSelectedGIN] = useState(null);
  const [ginFormData, setGinFormData] = useState({
    ginNumber: '',
    ginDate: todayISO(),
    warehouseId: '',
    referenceNumber: '',
    customerName: '',
    department: '',
    remarks: '',
    status: 'DRAFT',
    lineItems: []
  });
  const [ginLineItems, setGinLineItems] = useState([{ itemId: '', quantity: '', unitOfMeasurementId: '', issuedQuantity: '', remarks: '' }]);

  // Vendor Type state
  const [vendorTypes, setVendorTypes] = useState([]);
  const [showVendorTypeModal, setShowVendorTypeModal] = useState(false);
  const [selectedVendorType, setSelectedVendorType] = useState(null);
  const [vendorTypeFormData, setVendorTypeFormData] = useState({
    name: '',
    description: '',
    isActive: true
  });

  // Vendor state
  const [vendors, setVendors] = useState([]);
  const [showVendorModal, setShowVendorModal] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [showVendorTransactions, setShowVendorTransactions] = useState(false);
  const [vendorTransactions, setVendorTransactions] = useState([]);
  const [loadingVendorTransactions, setLoadingVendorTransactions] = useState(false);
  const [vendorFormData, setVendorFormData] = useState({
    name: '',
    email: '',
    phoneNumber: '',
    companyName: '',
    contactPerson: '',
    vendorTypeId: '',
    address: '',
    city: '',
    district: '',
    state: '',
    country: '',
    postalCode: '',
    taxId: '',
    gstRegistrationType: '',
    paymentTerms: '',
    creditDays: '',
    msmeCategory: '',
    udyamNumber: '',
    creditLimit: '',
    bankAccount: '',
    bankName: '',
    ifscCode: '',
    website: '',
    notes: '',
    isActive: true,
    pan: '',
    tdsSection: ''
  });
  // TDS sections, when the organization deducts TDS (otherwise the vendor form shows nothing about it)
  const [tdsSettings, setTdsSettings] = useState(null);
  const [selectedVendorCountryCode, setSelectedVendorCountryCode] = useState('');
  const [selectedPOCountryCode, setSelectedPOCountryCode] = useState('');
  const [selectedWBRCountryCode, setSelectedWBRCountryCode] = useState('');

  // Purchase Order state
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [showPOModal, setShowPOModal] = useState(false);
  const [selectedPO, setSelectedPO] = useState(null);
  const [services, setServices] = useState([]);
  const [poFormData, setPoFormData] = useState({
    poNumber: '',
    poDate: todayISO(),
    warehouseId: '',
    requiredDate: '',
    referenceNumber: '',
    addressOfOrigin: '',
    cityOfOrigin: '',
    districtOfOrigin: '',
    stateOfOrigin: '',
    countryOfOrigin: '',
    remarks: '',
    status: 'DRAFT'
  });
  const [poLineItems, setPoLineItems] = useState([{ 
    type: 'ITEM', // 'ITEM' or 'SERVICE'
    itemId: '', 
    serviceId: '',
    vendorId: '',
    quantity: '', 
    unitPrice: '',
    unitOfMeasurementId: '', 
    description: '',
    remarks: '',
    linkedMaterialLineItemIndex: '' // Index of the material line item this service is linked to
  }]);
  const [showPOTransactionsModal, setShowPOTransactionsModal] = useState(false);
  const [selectedPOForTransactions, setSelectedPOForTransactions] = useState(null);
  const [poTransactions, setPoTransactions] = useState({ deliveries: [], payments: [] });
  const [loadingPOTransactions, setLoadingPOTransactions] = useState(false);

  // Weigh Bridge Receipt state
  const [weighBridgeReceipts, setWeighBridgeReceipts] = useState([]);
  const [showWBRModal, setShowWBRModal] = useState(false);
  const [selectedWBR, setSelectedWBR] = useState(null);
  const [users, setUsers] = useState([]); // For weigh bridge operator dropdown
  const [openProcurementSurveys, setOpenProcurementSurveys] = useState([]);
  
  // Helper function to get UOM for a WBR
  const getWBRUOM = (wbr) => {
    // Try to get UOM from purchase order line items if available
    if (wbr.purchaseOrder && wbr.purchaseOrder.lineItems && wbr.material) {
      const matchingLineItem = wbr.purchaseOrder.lineItems.find(
        li => li.item && li.item.id === wbr.material.id
      );
      if (matchingLineItem && matchingLineItem.unitOfMeasurement) {
        return matchingLineItem.unitOfMeasurement.symbol;
      }
    }
    if (wbr.procurementSurvey?.unitOfMeasurement?.symbol) {
      return wbr.procurementSurvey.unitOfMeasurement.symbol;
    }
    // Default to 'kg' for weight measurements
    return 'kg';
  };
  const [wbrFormData, setWbrFormData] = useState({
    rstNumber: '',
    vendorId: '',
    transporterVendorId: '',
    addressOfOrigin: '',
    address: '',
    city: '',
    district: '',
    state: '',
    country: '',
    pinCode: '',
    referenceType: '', // '' | 'PURCHASE_ORDER' | 'PROCUREMENT_SURVEY'
    purchaseOrderId: '',
    procurementSurveyId: '',
    vehicleNo: '',
    materialItemId: '',
    grossWeight: '',
    emptyWeight: '',
    receiptDate: todayISO(),
    receiptTime: new Date().toTimeString().slice(0, 5), // HH:MM format
    weighBridgeOperatorId: '',
    remarks: ''
  });

  // Load Purchase tabs on demand
  useEffect(() => {
    if (activeTab === 'vendor-types') {
      fetchVendorTypes();
    } else if (activeTab === 'vendors') {
      fetchVendors();
      if (vendorTypes.length === 0) fetchVendorTypes();
    } else if (activeTab === 'purchase-orders') {
      fetchPurchaseOrders();
      fetchWarehouses();
      fetchOrganizationCurrency();
      fetchOrganizationMaterials();
      if (vendors.length === 0) fetchVendors();
      if (services.length === 0) fetchServices();
      if (uoms.length === 0) fetchUOMs();
      if (items.length === 0) fetchItems();
    } else if (activeTab === 'weigh-bridge-receipts') {
      fetchWeighBridgeReceipts();
      fetchPurchaseOrders();
      fetchOpenProcurementSurveys();
      fetchOrganizationMaterials();
      if (users.length === 0) fetchUsers();
      if (vendors.length === 0) fetchVendors();
    }
    // Intentionally only re-run on tab change; fetchers close over latest state
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    setLoading(false);
  }, []);

  // Sync linkedMaterialLineItemIndex for service items when grnLineItems changes
  useEffect(() => {
    if (grnServiceItems.length > 0 && grnLineItems.length > 0) {
      let hasChanges = false;
      const updated = grnServiceItems.map((si, idx) => {
        // If service has linkedMaterialItemId but no linkedMaterialLineItemIndex, find it
        if (si.linkedMaterialItemId && (!si.linkedMaterialLineItemIndex || si.linkedMaterialLineItemIndex === '')) {
          const serviceItemId = String(si.linkedMaterialItemId);
          const foundIndex = grnLineItems.findIndex(li => {
            if (!li.itemId) return false;
            return String(li.itemId) === serviceItemId;
          });
          
          if (foundIndex >= 0) {
            hasChanges = true;
            return {
              ...si,
              linkedMaterialLineItemIndex: foundIndex.toString()
            };
          }
        }
        return si;
      });
      
      if (hasChanges) {
        setGrnServiceItems(updated);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grnLineItems.map(li => `${li.itemId || ''}-${li.receivedQuantity || ''}`).join(',')]);

  const fetchWarehouses = async () => {
    try {
      const response = await axios.get('/api/inventory/warehouses');
      setWarehouses(response.data);
    } catch (error) {
      // Only show toast if not already showing another error
      if (activeTab !== 'stock-levels') {
        toast.error('Error fetching warehouses');
      }
      console.error(error);
    }
  };

  const fetchStockLevels = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/inventory/stock-levels');
      setStockLevels(response.data || []);
    } catch (error) {
      toast.error('Error fetching stock levels: ' + (error.response?.data?.message || error.message || 'Unknown error'));
      console.error('Stock levels error:', error);
      setStockLevels([]); // Set empty array on error to prevent infinite loading
    } finally {
      setLoading(false);
    }
  };

  const fetchStockTransactions = async (warehouseId, itemId, itemName, warehouseName) => {
    try {
      setLoading(true);
      // Ensure organization currency is loaded before displaying transactions
      await fetchOrganizationCurrency();
      const response = await axios.get('/api/inventory/stock-transactions', {
        params: { warehouseId, itemId }
      });
      setStockTransactions(response.data || []);
      setSelectedStockItem({ itemName, warehouseName, warehouseId, itemId });
      setShowStockTransactions(true);
    } catch (error) {
      toast.error('Error fetching stock transactions: ' + (error.response?.data?.message || error.message || 'Unknown error'));
      console.error('Stock transactions error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBackToStockLevels = () => {
    setShowStockTransactions(false);
    setStockTransactions([]);
    setSelectedStockItem(null);
  };

  const fetchGRNs = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/inventory/grns');
      // Sort by grnDate in reverse chronological order (latest first)
      const sortedData = [...response.data].sort((a, b) => {
        const dateA = new Date(a.grnDate);
        const dateB = new Date(b.grnDate);
        return dateB - dateA; // Descending order (latest first)
      });
      setGrns(sortedData);
    } catch (error) {
      toast.error('Error fetching GRNs');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchGINs = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/inventory/gins');
      // Sort by ginDate in reverse chronological order (latest first)
      const sortedData = [...response.data].sort((a, b) => {
        const dateA = new Date(a.ginDate);
        const dateB = new Date(b.ginDate);
        return dateB - dateA; // Descending order (latest first)
      });
      setGins(sortedData);
    } catch (error) {
      toast.error('Error fetching GINs');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchItems = async () => {
    try {
      const response = await axios.get('/api/inventory/items');
      setItems(response.data);
    } catch (error) {
      console.error('Error fetching items:', error);
    }
  };

  const fetchOrganizationMaterials = async () => {
    try {
      const response = await axios.get('/api/organization-materials');
      setOrganizationMaterials(response.data || []);
    } catch (error) {
      console.error('Error fetching organization materials:', error);
    }
  };

  const fetchUOMs = async () => {
    try {
      const response = await axios.get('/api/inventory/units-of-measurement');
      setUoms(response.data);
    } catch (error) {
      console.error('Error fetching UOMs:', error);
      toast.error('Error fetching units of measurement');
    }
  };

  const fetchVendorTypes = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/inventory/vendor-types');
      setVendorTypes(response.data);
    } catch (error) {
      toast.error('Error fetching vendor types');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchVendors = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/inventory/vendors');
      setVendors(response.data);
    } catch (error) {
      toast.error('Error fetching vendors');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateVendorType = () => {
    setVendorTypeFormData({
      name: '',
      description: '',
      isActive: true
    });
    setSelectedVendorType(null);
    setShowVendorTypeModal(true);
  };

  const handleEditVendorType = (vendorType) => {
    setVendorTypeFormData({
      name: vendorType.name,
      description: vendorType.description || '',
      isActive: vendorType.isActive
    });
    setSelectedVendorType(vendorType);
    setShowVendorTypeModal(true);
  };

  const handleDeleteVendorType = async (id) => {
    if (!window.confirm('Are you sure you want to delete this vendor type?')) {
      return;
    }
    try {
      await axios.delete(`/api/inventory/vendor-types/${id}`);
      toast.success('Vendor type deleted successfully');
      fetchVendorTypes();
    } catch (error) {
      const errorMsg = error.response?.data || 'Error deleting vendor type';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Error deleting vendor type');
    }
  };

  const handleSaveVendorType = async (e) => {
    e.preventDefault();
    try {
      if (selectedVendorType) {
        await axios.put(`/api/inventory/vendor-types/${selectedVendorType.id}`, vendorTypeFormData);
        toast.success('Vendor type updated successfully');
      } else {
        await axios.post('/api/inventory/vendor-types', vendorTypeFormData);
        toast.success('Vendor type created successfully');
      }
      setShowVendorTypeModal(false);
      fetchVendorTypes();
    } catch (error) {
      const errorMsg = error.response?.data || 'Error saving vendor type';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Error saving vendor type');
    }
  };

  const loadTdsSettings = () => {
    axios.get('/api/tds/settings').then((r) => setTdsSettings(r.data)).catch(() => setTdsSettings(null));
  };

  const handleCreateVendor = async () => {
    const defaults = await ensureOrgAddressDefaults();
    setVendorFormData({
      name: '',
      email: '',
      phoneNumber: '',
      companyName: '',
      contactPerson: '',
      vendorTypeId: '',
      address: '',
      city: '',
      district: '',
      state: defaults.state,
      country: defaults.country,
      postalCode: '',
      taxId: '',
      gstRegistrationType: '',
      paymentTerms: '',
      creditDays: '',
      msmeCategory: '',
      udyamNumber: '',
      creditLimit: '',
      bankAccount: '',
      bankName: '',
      ifscCode: '',
      website: '',
      notes: '',
      isActive: true,
      pan: '',
      tdsSection: ''
    });
    loadTdsSettings();
    setSelectedVendorCountryCode(defaults.countryCode);
    setSelectedVendor(null);
    setShowVendorModal(true);
  };

  const handleEditVendor = (vendor) => {
    // Find country code from country name
    const countryName = vendor.country || '';
    const countryObj = countries.find(c => c.name === countryName);
    const countryCode = countryObj ? countryObj.code : '';
    
    setSelectedVendorCountryCode(countryCode);
    setVendorFormData({
      name: vendor.name,
      email: vendor.email || '',
      phoneNumber: vendor.phoneNumber || '',
      companyName: vendor.companyName || '',
      contactPerson: vendor.contactPerson || '',
      vendorTypeId: vendor.vendorType?.id || '',
      address: vendor.address || '',
      city: vendor.city || '',
      district: vendor.district || '',
      state: vendor.state || '',
      country: vendor.country || '',
      postalCode: vendor.postalCode || '',
      taxId: vendor.taxId || '',
      gstRegistrationType: vendor.gstRegistrationType || '',
      paymentTerms: vendor.paymentTerms || '',
      creditDays: vendor.creditDays ?? '',
      msmeCategory: vendor.msmeCategory || '',
      udyamNumber: vendor.udyamNumber || '',
      creditLimit: vendor.creditLimit || '',
      bankAccount: vendor.bankAccount || '',
      bankName: vendor.bankName || '',
      ifscCode: vendor.ifscCode || '',
      website: vendor.website || '',
      notes: vendor.notes || '',
      isActive: vendor.isActive,
      pan: vendor.pan || '',
      tdsSection: vendor.tdsSection || ''
    });
    loadTdsSettings();
    setSelectedVendor(vendor);
    setShowVendorModal(true);
  };

  const handleViewVendorTransactions = async (vendor) => {
    setSelectedVendor(vendor);
    setLoadingVendorTransactions(true);
    try {
      // Ensure organization currency is loaded before displaying transactions
      await fetchOrganizationCurrency();
      const response = await axios.get(`/api/inventory/vendors/${vendor.id}/transactions`);
      setVendorTransactions(response.data || []);
      setShowVendorTransactions(true);
    } catch (error) {
      toast.error('Error fetching vendor transactions');
      console.error(error);
    } finally {
      setLoadingVendorTransactions(false);
    }
  };

  const handleBackToVendors = () => {
    setShowVendorTransactions(false);
    setVendorTransactions([]);
    setSelectedVendor(null);
  };

  const handleViewVendorTransactionDocument = async (transaction) => {
    if (!selectedVendor?.id || !transaction?.id || !transaction?.type || !transaction?.reference) {
      return;
    }
    try {
      const response = await axios.get(
        `/api/inventory/vendors/${selectedVendor.id}/transactions/${transaction.type}/${transaction.id}`
      );
      if (transaction.type === 'GRN') {
        await handleViewPrint(response.data, 'GRN');
      } else if (transaction.type === 'PAYMENT') {
        await handleViewPrint(response.data, 'VENDOR_PAYMENT');
      }
    } catch (error) {
      const errorMsg = error.response?.data || 'Failed to open reference document';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Failed to open reference document');
    }
  };

  const handleDeleteVendor = async (id) => {
    if (!window.confirm('Are you sure you want to delete this vendor?')) {
      return;
    }
    try {
      await axios.delete(`/api/inventory/vendors/${id}`);
      toast.success('Vendor deleted successfully');
      fetchVendors();
    } catch (error) {
      const errorMsg = error.response?.data || 'Error deleting vendor';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Error deleting vendor');
    }
  };

  /** A found GSTIN fills in what the vendor form asks for, without overwriting anything already typed. */
  const handleVendorGstinResolved = (result) => {
    setSelectedVendorCountryCode('IN');
    const address = [result.buildingNo, result.street, result.locality].filter(Boolean).join(', ');
    setVendorFormData((prev) => ({
      ...prev,
      companyName: prev.companyName || result.legalName || result.tradeName || prev.companyName,
      name: prev.name || result.tradeName || result.legalName || prev.name,
      address: address || prev.address,
      city: result.city || prev.city,
      district: result.district || prev.district,
      postalCode: result.pincode || prev.postalCode,
      country: 'India',
      state: matchIndianState(result.state) || prev.state,
      gstRegistrationType: prev.gstRegistrationType || 'REGULAR'
    }));
  };

  const indianVendor = selectedVendorCountryCode === 'IN';

  const handleSaveVendor = async (e) => {
    e.preventDefault();
    const vendorGstin = (vendorFormData.taxId || '').trim();
    if (indianVendor && vendorGstin && !GSTIN_PATTERN.test(vendorGstin.toUpperCase())) {
      toast.error('GSTIN doesn\'t look valid - fix it or clear the field before saving');
      return;
    }
    const requiredChecks = [
      { ok: !!vendorFormData.vendorTypeId, message: 'Vendor Type is required' },
      { ok: !!(vendorFormData.phoneNumber && vendorFormData.phoneNumber.trim()), message: 'Phone Number is required' },
      { ok: !!(vendorFormData.city && vendorFormData.city.trim()), message: 'City is required' },
      { ok: !!(vendorFormData.state && vendorFormData.state.trim()), message: 'State/Province is required' },
      { ok: !!(vendorFormData.country && vendorFormData.country.trim()), message: 'Country is required' }
    ];
    const failed = requiredChecks.find((c) => !c.ok);
    if (failed) {
      toast.error(failed.message);
      return;
    }
    const website = checkWebAddress('Website', vendorFormData.website);
    if (website.error) {
      toast.error(website.error);
      return;
    }
    try {
      const payload = {
        ...vendorFormData,
        website: website.value,
        vendorType: { id: parseInt(vendorFormData.vendorTypeId) },
        phoneNumber: vendorFormData.phoneNumber.trim(),
        city: vendorFormData.city.trim(),
        state: vendorFormData.state.trim(),
        country: vendorFormData.country.trim(),
        creditLimit: vendorFormData.creditLimit ? parseFloat(vendorFormData.creditLimit) : null,
        creditDays: vendorFormData.creditDays === '' || vendorFormData.creditDays == null ? null : parseInt(vendorFormData.creditDays, 10),
        msmeCategory: vendorFormData.msmeCategory || null,
        udyamNumber: vendorFormData.msmeCategory ? (vendorFormData.udyamNumber || null) : null
      };
      delete payload.vendorTypeId;

      if (selectedVendor) {
        await axios.put(`/api/inventory/vendors/${selectedVendor.id}`, payload);
        toast.success('Vendor updated successfully');
      } else {
        await axios.post('/api/inventory/vendors', payload);
        toast.success('Vendor created successfully');
      }
      if (indianVendor && !vendorGstin && vendorFormData.gstRegistrationType !== 'UNREGISTERED') {
        toast.warn('Saved without a GSTIN. Credit on this vendor\'s invoices can\'t be claimed until you add one.');
      }
      setShowVendorModal(false);
      fetchVendors();
    } catch (error) {
      const errorMsg = error.response?.data || 'Error saving vendor';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Error saving vendor');
    }
  };

  const fetchServices = async () => {
    try {
      const response = await axios.get('/api/inventory/services');
      setServices(response.data);
    } catch (error) {
      console.error('Error fetching services:', error);
      toast.error('Error fetching services');
    }
  };

  const fetchOrganizationCurrency = async () => {
    try {
      if (user?.organizationId) {
        const response = await axios.get(`/api/organizations/${user.organizationId}`);
        const currency = response.data?.defaultCurrency || 'USD';
        setOrganizationCurrency(currency);
        setOrgAddressDefaults(getOrgAddressDefaults(response.data));
      }
    } catch (error) {
      console.error('Error fetching organization currency:', error);
      // Keep default USD on error
    }
  };

  const ensureOrgAddressDefaults = async () => {
    if (orgAddressDefaults.country || orgAddressDefaults.state) {
      return orgAddressDefaults;
    }
    const defaults = await loadOrgAddressDefaults(user?.organizationId);
    setOrgAddressDefaults(defaults);
    return defaults;
  };

  // Helper function to format currency
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) {
      return `${organizationCurrency} 0.00`;
    }
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    return `${organizationCurrency} ${numAmount.toFixed(2)}`;
  };

  const fetchPurchaseOrders = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/inventory/purchase-orders');
      // Sort by poDate in reverse chronological order (latest first)
      const sortedData = [...response.data].sort((a, b) => {
        const dateA = new Date(a.poDate);
        const dateB = new Date(b.poDate);
        return dateB - dateA; // Descending order (latest first)
      });
      setPurchaseOrders(sortedData);
    } catch (error) {
      toast.error('Error fetching purchase orders');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePO = async () => {
    // Fetch currency before opening modal
    if (!organizationCurrency || organizationCurrency === 'USD') {
      await fetchOrganizationCurrency();
    }
    const defaults = await ensureOrgAddressDefaults();

    setPoFormData({
      poNumber: '',
      poDate: todayISO(),
      warehouseId: '',
      requiredDate: '',
      referenceNumber: '',
      addressOfOrigin: '',
      cityOfOrigin: '',
      districtOfOrigin: '',
      stateOfOrigin: defaults.state,
      countryOfOrigin: defaults.country,
      remarks: '',
      status: 'DRAFT'
    });
    setSelectedPOCountryCode(defaults.countryCode);
    setPoLineItems([{ 
      type: 'ITEM',
      itemId: '', 
      serviceId: '',
      vendorId: '',
      quantity: '', 
      unitPrice: '',
      unitOfMeasurementId: '', 
      description: '',
      remarks: '' 
    }]);
    setSelectedPO(null);
    setShowPOModal(true);
  };

  /** A purchase order for the materials picked from the running-low list, at the vendor and rate each was last bought at. */
  const handleMakePOFromLowStock = async (rows) => {
    await handleCreatePO();
    setPoFormData((prev) => ({ ...prev, remarks: 'Restocking materials running low' }));
    setPoLineItems(rows.map((r) => ({
      type: 'ITEM',
      itemId: String(r.itemId),
      serviceId: '',
      vendorId: r.vendorId ? String(r.vendorId) : '',
      quantity: String(r.orderQuantity),
      unitPrice: r.lastPrice != null ? String(Number(r.lastPrice)) : '',
      unitOfMeasurementId: r.uomId ? String(r.uomId) : '',
      description: '',
      remarks: ''
    })));
    if (rows.some((r) => !r.vendorId)) toast.info('Choose a vendor for the items not bought before, and the warehouse.');
  };

  const handleViewPrint = async (data, type) => {
    // Ensure organization currency is loaded before showing print view
    await fetchOrganizationCurrency();
    
    // For GRN, separate material and service line items
    if (type === 'GRN' && data.lineItems) {
      data.materialLineItems = data.lineItems.filter(li => !li.lineType || li.lineType === 'MATERIAL');
      data.serviceItems = data.lineItems.filter(li => li.lineType === 'SERVICE').map(li => ({
        service: li.service,
        vendor: li.vendor,
        quantity: li.receivedQuantity,
        unitPrice: li.unitPrice,
        lineTotal: li.lineTotal,
        unitOfMeasurement: li.unitOfMeasurement,
        vendorLotNumber: li.vendorLotNumber,
        internalLotNumber: li.internalLotNumber
      }));
      // Keep lineItems as material items for backward compatibility
      data.lineItems = data.materialLineItems;
    }
    
    setPrintData(data);
    setPrintType(type);
    setShowPrintModal(true);
  };

  const handlePrint = () => {
    window.print();
  };

  const handleEditPO = async (po) => {
    const status = (po.status || '').toUpperCase();
    if (status !== 'DRAFT' && status !== 'APPROVED') {
      toast.error(`Cannot edit purchase order with status: ${po.status}`);
      return;
    }
    if (status === 'APPROVED') {
      try {
        const response = await axios.get(`/api/inventory/purchase-orders/${po.id}/transactions`);
        const deliveries = (response.data?.deliveries || []).filter(
          d => (d.status || '').toUpperCase() !== 'CANCELLED'
        );
        if (deliveries.length > 0) {
          toast.error('Cannot edit purchase order after goods have been received against it');
          return;
        }
      } catch (error) {
        toast.error('Unable to verify PO receipts before editing');
        return;
      }
    }

    setPoFormData({
      poNumber: po.poNumber,
      poDate: po.poDate,
      warehouseId: po.warehouse?.id || '',
      requiredDate: po.requiredDate || '',
      referenceNumber: po.referenceNumber || '',
      addressOfOrigin: po.addressOfOrigin || '',
      cityOfOrigin: po.cityOfOrigin || '',
      districtOfOrigin: po.districtOfOrigin || '',
      stateOfOrigin: po.stateOfOrigin || '',
      countryOfOrigin: po.countryOfOrigin || '',
      remarks: po.remarks || '',
      status: po.status
    });
    
    // Find country code from country name
    const countryName = po.countryOfOrigin || '';
    const countryObj = countries.find(c => c.name === countryName);
    const countryCode = countryObj ? countryObj.code : '';
    setSelectedPOCountryCode(countryCode);
    
    // Map line items
    const mappedLineItems = po.lineItems.map((li, idx) => {
      const mapped = {
        type: li.item ? 'ITEM' : 'SERVICE',
        itemId: li.item?.id || '',
        serviceId: li.service?.id || '',
        vendorId: li.vendor?.id || '',
        quantity: li.quantity || '',
        unitPrice: li.unitPrice || '',
        unitOfMeasurementId: li.unitOfMeasurement?.id || '',
        description: li.description || '',
        remarks: li.remarks || '',
        linkedMaterialLineItemIndex: ''
      };
      
      // If this is a service line item with a linked material item, find its index
      if (li.service && li.linkedMaterialLineItem && li.linkedMaterialLineItem.id) {
        const linkedIdx = po.lineItems.findIndex(item => item.id === li.linkedMaterialLineItem.id);
        if (linkedIdx !== -1) {
          mapped.linkedMaterialLineItemIndex = linkedIdx.toString();
        }
      }
      
      return mapped;
    });
    
    setPoLineItems(mappedLineItems.length > 0 ? mappedLineItems : [{ 
      type: 'ITEM',
      itemId: '', 
      serviceId: '',
      vendorId: '',
      quantity: '', 
      unitPrice: '',
      unitOfMeasurementId: '', 
      description: '',
      remarks: '' 
    }]);
    
    setSelectedPO(po);
    setShowPOModal(true);
  };

  const handleDeletePO = async (id) => {
    if (!window.confirm('Are you sure you want to delete this purchase order?')) {
      return;
    }
    try {
      await axios.delete(`/api/inventory/purchase-orders/${id}`);
      toast.success('Purchase order deleted successfully');
      fetchPurchaseOrders();
    } catch (error) {
      const errorMsg = error.response?.data || 'Error deleting purchase order';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Error deleting purchase order');
    }
  };

  const handleApprovePO = async (po) => {
    if (!window.confirm(`Approve purchase order ${po.poNumber || ''}?`)) {
      return;
    }
    try {
      await axios.post(`/api/inventory/purchase-orders/${po.id}/approve`);
      toast.success('Purchase order approved successfully');
      fetchPurchaseOrders();
    } catch (error) {
      const errorMsg = error.response?.data || 'Error approving purchase order';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Error approving purchase order');
    }
  };

  const handleViewPOTransactions = async (po) => {
    setSelectedPOForTransactions(po);
    setShowPOTransactionsModal(true);
    setLoadingPOTransactions(true);
    setPoTransactions({ deliveries: [], payments: [] });
    try {
      const response = await axios.get(`/api/inventory/purchase-orders/${po.id}/transactions`);
      setPoTransactions(response.data || { deliveries: [], payments: [] });
    } catch (error) {
      toast.error(error.response?.data || 'Failed to load PO transactions');
      setPoTransactions({ deliveries: [], payments: [] });
    } finally {
      setLoadingPOTransactions(false);
    }
  };

  const handleAddPOLineItem = () => {
    setPoLineItems([...poLineItems, { 
      type: 'ITEM',
      itemId: '', 
      serviceId: '',
      vendorId: '',
      quantity: '', 
      unitPrice: '',
      unitOfMeasurementId: '', 
      description: '',
      remarks: '',
      linkedMaterialLineItemIndex: ''
    }]);
  };

  const handleRemovePOLineItem = (index) => {
    setPoLineItems(poLineItems.filter((_, i) => i !== index));
  };

  // Each line's vendor's last agreed rate for its item or service
  const poVendorRates = useVendorRates(showPOModal ? poLineItems.map((li) => li.vendorId) : []);
  const lastPoRate = (li) => {
    const rates = li.vendorId ? poVendorRates[String(li.vendorId)] : null;
    if (!rates) return null;
    return li.type === 'SERVICE' ? rates.services?.[li.serviceId] : rates.items?.[li.itemId];
  };

  // A vendor's rates arrive after the vendor is picked: fill lines that have no rate yet
  useEffect(() => {
    if (!showPOModal) return;
    setPoLineItems((prev) => {
      let changed = false;
      const next = prev.map((li) => {
        const last = lastPoRate(li);
        if ((li.unitPrice === '' || li.unitPrice == null) && last?.rate != null) {
          changed = true;
          return { ...li, unitPrice: String(Number(last.rate)) };
        }
        return li;
      });
      return changed ? next : prev;
    });
    // lastPoRate reads poVendorRates, which is what this follows
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poVendorRates, showPOModal]);

  const handlePOLineItemChange = (index, field, value) => {
    const updated = [...poLineItems];
    updated[index] = { ...updated[index], [field]: value };
    
    // Reset item/service when type changes
    if (field === 'type') {
      updated[index].itemId = '';
      updated[index].serviceId = '';
      updated[index].linkedMaterialLineItemIndex = ''; // Reset link when type changes
    }

    // A new item, service or vendor starts from that vendor's last rate for it
    if (field === 'itemId' || field === 'serviceId' || field === 'vendorId') {
      const last = lastPoRate(updated[index]);
      if (last?.rate != null) updated[index].unitPrice = String(Number(last.rate));
    }
    
    setPoLineItems(updated);
  };

  const handleSavePO = async (e) => {
    e.preventDefault();
    try {
      // Map form-row index → payload line number (1-based) among valid rows only
      const validRows = poLineItems
        .map((li, formIndex) => ({ li, formIndex }))
        .filter(({ li }) => (li.itemId || li.serviceId) && li.vendorId && li.quantity && li.unitPrice);

      const formIndexToLineNumber = {};
      validRows.forEach(({ formIndex }, idx) => {
        formIndexToLineNumber[formIndex] = idx + 1;
      });

      const lineItems = validRows.map(({ li }, idx) => {
        const lineItem = {
          lineNumber: idx + 1,
          item: li.itemId ? { id: parseInt(li.itemId, 10) } : null,
          service: li.serviceId ? { id: parseInt(li.serviceId, 10) } : null,
          vendor: { id: parseInt(li.vendorId, 10) },
          quantity: parseFloat(li.quantity),
          unitPrice: parseFloat(li.unitPrice),
          unitOfMeasurement: li.unitOfMeasurementId ? { id: parseInt(li.unitOfMeasurementId, 10) } : null,
          description: li.description || ''
        };

        // Link services by line number in this payload (IDs are recreated on update)
        if (li.serviceId && li.linkedMaterialLineItemIndex !== '' && li.linkedMaterialLineItemIndex != null) {
          const linkedFormIndex = parseInt(li.linkedMaterialLineItemIndex, 10);
          const linkedLineNumber = formIndexToLineNumber[linkedFormIndex];
          if (!linkedLineNumber) {
            toast.error(`Service on line ${idx + 1}: linked material line is missing or incomplete`);
            throw new Error('INVALID_LINKED_MATERIAL');
          }
          const linkedRow = validRows[linkedLineNumber - 1]?.li;
          if (!linkedRow?.itemId) {
            toast.error(`Service on line ${idx + 1}: linked row must be a material (item) line`);
            throw new Error('INVALID_LINKED_MATERIAL');
          }
          lineItem.linkedMaterialLineItem = { lineNumber: linkedLineNumber };
        }

        return lineItem;
      });

      if (lineItems.length === 0) {
        toast.error('Please add at least one line item');
        return;
      }

      const payload = {
        poNumber: poFormData.poNumber || null,
        poDate: poFormData.poDate,
        warehouse: { id: parseInt(poFormData.warehouseId) },
        requiredDate: poFormData.requiredDate || null,
        referenceNumber: poFormData.referenceNumber || null,
        addressOfOrigin: poFormData.addressOfOrigin || null,
        cityOfOrigin: poFormData.cityOfOrigin || null,
        districtOfOrigin: poFormData.districtOfOrigin || null,
        stateOfOrigin: poFormData.stateOfOrigin || null,
        countryOfOrigin: poFormData.countryOfOrigin || null,
        remarks: poFormData.remarks || null,
        // New POs always start as DRAFT; status advances via approval / fulfillment
        status: selectedPO ? (poFormData.status || selectedPO.status || 'DRAFT') : 'DRAFT',
        lineItems: lineItems
      };

      if (selectedPO) {
        await axios.put(`/api/inventory/purchase-orders/${selectedPO.id}`, payload);
        toast.success('Purchase order updated successfully');
      } else {
        await axios.post('/api/inventory/purchase-orders', payload);
        toast.success('Purchase order created successfully');
      }
      setShowPOModal(false);
      fetchPurchaseOrders();
    } catch (error) {
      if (error?.message === 'INVALID_LINKED_MATERIAL') {
        return;
      }
      const errorMsg = error.response?.data || 'Error saving purchase order';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Error saving purchase order');
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get('/api/users');
      setUsers(response.data.filter(u => u.isActive));
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchWeighBridgeReceipts = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/inventory/weigh-bridge-receipts');
      // Sort by receiptDate and receiptTime in reverse chronological order (latest first)
      const sortedData = [...response.data].sort((a, b) => {
        const dateA = new Date(a.receiptDate + ' ' + (a.receiptTime || '00:00'));
        const dateB = new Date(b.receiptDate + ' ' + (b.receiptTime || '00:00'));
        return dateB - dateA; // Descending order (latest first)
      });
      setWeighBridgeReceipts(sortedData);
    } catch (error) {
      toast.error('Error fetching weigh bridge receipts');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchOpenProcurementSurveys = async () => {
    try {
      const response = await axios.get('/api/inventory/procurement-surveys', { params: { status: 'OPEN' } });
      setOpenProcurementSurveys(response.data || []);
    } catch (error) {
      console.error('Error fetching open procurement surveys:', error);
      setOpenProcurementSurveys([]);
    }
  };

  const handleCreateWBR = async () => {
    const defaults = await ensureOrgAddressDefaults();
    const now = new Date();
    setWbrFormData({
      rstNumber: '',
      vendorId: '',
      transporterVendorId: '',
      addressOfOrigin: '',
      address: '',
      city: '',
      district: '',
      state: defaults.state,
      country: defaults.country,
      pinCode: '',
      referenceType: '',
      purchaseOrderId: '',
      procurementSurveyId: '',
      vehicleNo: '',
      materialItemId: '',
      grossWeight: '',
      emptyWeight: '',
      receiptDate: localISODate(now),
      receiptTime: now.toTimeString().slice(0, 5),
      weighBridgeOperatorId: user?.id || '',
      remarks: ''
    });
    setSelectedWBRCountryCode(defaults.countryCode);
    if (purchaseOrders.length === 0) {
      fetchPurchaseOrders();
    }
    fetchOpenProcurementSurveys();
    setSelectedWBR(null);
    setShowWBRModal(true);
  };

  const handleEditWBR = (wbr) => {
    // Find country code from country name
    const countryName = wbr.country || '';
    const countryObj = countries.find(c => c.name === countryName);
    const countryCode = countryObj ? countryObj.code : '';
    
    setSelectedWBRCountryCode(countryCode);
    const referenceType = wbr.procurementSurvey?.id
      ? 'PROCUREMENT_SURVEY'
      : (wbr.purchaseOrder?.id ? 'PURCHASE_ORDER' : '');
    setWbrFormData({
      rstNumber: wbr.rstNumber || '',
      vendorId: wbr.vendor?.id || '',
      transporterVendorId: wbr.transporter?.id || '',
      addressOfOrigin: wbr.addressOfOrigin || '',
      address: wbr.address || '',
      city: wbr.city || '',
      district: wbr.district || '',
      state: wbr.state || '',
      country: wbr.country || '',
      pinCode: wbr.pinCode || '',
      referenceType,
      purchaseOrderId: wbr.purchaseOrder?.id || '',
      procurementSurveyId: wbr.procurementSurvey?.id || '',
      vehicleNo: wbr.vehicleNo || '',
      materialItemId: wbr.material?.id || '',
      grossWeight: wbr.grossWeight || '',
      emptyWeight: wbr.emptyWeight || '',
      receiptDate: wbr.receiptDate || todayISO(),
      receiptTime: wbr.receiptTime || new Date().toTimeString().slice(0, 5),
      weighBridgeOperatorId: wbr.weighBridgeOperator?.id || '',
      remarks: wbr.remarks || ''
    });
    if (purchaseOrders.length === 0) {
      fetchPurchaseOrders();
    }
    fetchOpenProcurementSurveys();
    setSelectedWBR(wbr);
    setShowWBRModal(true);
  };

  // Helper function to check if a PO matches the vendor and material filter
  const isPOMatchingFilter = (po, vendorId, materialItemId) => {
    // If both vendor and material are not selected, show all POs
    if (!vendorId || !materialItemId) {
      return true;
    }
    
    // If PO has no line items, it doesn't match
    if (!po.lineItems || po.lineItems.length === 0) {
      return false;
    }
    
    // Check if PO has a line item with both the selected vendor and material
    return po.lineItems.some(lineItem => {
      // Line item must be a material item (not a service)
      if (!lineItem.item) {
        return false;
      }
      
      // Both vendor and material must match
      const vendorMatches = lineItem.vendor && lineItem.vendor.id === parseInt(vendorId);
      const materialMatches = lineItem.item.id === parseInt(materialItemId);
      
      return vendorMatches && materialMatches;
    });
  };

  const isSurveyMatchingFilter = (survey, vendorId, materialItemId) => {
    if (vendorId && survey.vendor?.id && String(survey.vendor.id) !== String(vendorId)) {
      return false;
    }
    if (materialItemId && survey.item?.id && String(survey.item.id) !== String(materialItemId)) {
      return false;
    }
    return true;
  };

  const handleDeleteWBR = async (id) => {
    if (!window.confirm('Are you sure you want to delete this weigh bridge receipt?')) {
      return;
    }
    try {
      await axios.delete(`/api/inventory/weigh-bridge-receipts/${id}`);
      toast.success('Weigh bridge receipt deleted successfully');
      fetchWeighBridgeReceipts();
    } catch (error) {
      const errorMsg = error.response?.data || 'Error deleting weigh bridge receipt';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Error deleting weigh bridge receipt');
    }
  };

  const handleSaveWBR = async (e) => {
    e.preventDefault();
    try {
      // Calculate net weight
      const grossWeight = parseFloat(wbrFormData.grossWeight);
      const emptyWeight = parseFloat(wbrFormData.emptyWeight);
      
      if (!grossWeight || !emptyWeight) {
        toast.error('Both Gross Weight and Empty Weight are required');
        return;
      }
      
      if (grossWeight <= emptyWeight) {
        toast.error('Gross Weight must be greater than Empty Weight');
        return;
      }

      if (wbrFormData.referenceType === 'PURCHASE_ORDER' && !wbrFormData.purchaseOrderId) {
        toast.error('Please select a Purchase Order');
        return;
      }
      if (wbrFormData.referenceType === 'PROCUREMENT_SURVEY' && !wbrFormData.procurementSurveyId) {
        toast.error('Please select a Procurement Survey');
        return;
      }

      const payload = {
        rstNumber: wbrFormData.rstNumber || null, // Auto-generated if empty
        vendor: wbrFormData.vendorId ? { id: parseInt(wbrFormData.vendorId) } : null,
        transporter: wbrFormData.transporterVendorId ? { id: parseInt(wbrFormData.transporterVendorId) } : null,
        addressOfOrigin: wbrFormData.addressOfOrigin || null,
        address: wbrFormData.address || null,
        city: wbrFormData.city || null,
        district: wbrFormData.district || null,
        state: wbrFormData.state || null,
        country: wbrFormData.country || null,
        pinCode: wbrFormData.pinCode || null,
        purchaseOrder: wbrFormData.referenceType === 'PURCHASE_ORDER' && wbrFormData.purchaseOrderId
          ? { id: parseInt(wbrFormData.purchaseOrderId) }
          : null,
        procurementSurvey: wbrFormData.referenceType === 'PROCUREMENT_SURVEY' && wbrFormData.procurementSurveyId
          ? { id: parseInt(wbrFormData.procurementSurveyId) }
          : null,
        vehicleNo: wbrFormData.vehicleNo || null,
        material: wbrFormData.materialItemId ? { id: parseInt(wbrFormData.materialItemId) } : null,
        grossWeight: grossWeight,
        emptyWeight: emptyWeight,
        receiptDate: wbrFormData.receiptDate,
        receiptTime: wbrFormData.receiptTime,
        weighBridgeOperator: { id: parseInt(wbrFormData.weighBridgeOperatorId) },
        remarks: wbrFormData.remarks || null
      };

      if (selectedWBR) {
        await axios.put(`/api/inventory/weigh-bridge-receipts/${selectedWBR.id}`, payload);
        toast.success('Weigh bridge receipt updated successfully');
      } else {
        await axios.post('/api/inventory/weigh-bridge-receipts', payload);
        toast.success('Weigh bridge receipt created successfully');
      }
      setShowWBRModal(false);
      fetchWeighBridgeReceipts();
    } catch (error) {
      const errorMsg = error.response?.data || 'Error saving weigh bridge receipt';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Error saving weigh bridge receipt');
    }
  };


  const fetchUnusedWeighBridgeReceipts = async (excludeGrnId = null) => {
    try {
      const params = {};
      if (excludeGrnId) {
        params.excludeGrnId = excludeGrnId;
      }
      const response = await axios.get('/api/inventory/weigh-bridge-receipts/unused', { params });
      const receipts = response.data || [];
      setUnusedWeighBridgeReceipts(receipts);
      return receipts;
    } catch (error) {
      console.error('Error fetching unused weigh bridge receipts:', error);
      setUnusedWeighBridgeReceipts([]);
      return [];
    }
  };

  const handleCreateGRN = () => {
    setGrnFormData({
      grnNumber: '',
      grnDate: todayISO(),
      warehouseId: '',
      purchaseOrderId: '',
      remarks: '',
      status: 'DRAFT',
      lineItems: []
    });
    const newInternalLotNumber = generateInternalLotNumber();
    setGrnLineItems([{ itemId: '', vendorId: '', unitOfMeasurementId: '', receivedQuantity: '', unitPrice: '', lineTotal: '', weighBridgeReceiptNo: '', weighBridgeReceiptId: '', vendorLotNumber: '', internalLotNumber: newInternalLotNumber, orderedQuantity: '', receivedQuantityTotal: '', pendingQuantity: '', uomSymbol: '', isFromPO: false, additionalCosts: '', allocatedServiceCost: 0, landedCost: 0, landedCostPerUnit: 0 }]);
    setGrnServiceItems([]);
    setSelectedGRN(null);
    // Ensure purchase orders are loaded for the dropdown
    if (purchaseOrders.length === 0) {
      fetchPurchaseOrders();
    }
    // Fetch unused weigh bridge receipts
    fetchUnusedWeighBridgeReceipts();
    setShowGRNModal(true);
  };

  const handleEditGRN = async (grn) => {
    setGrnFormData({
      grnNumber: grn.grnNumber,
      grnDate: grn.grnDate,
      warehouseId: grn.warehouse?.id || '',
      purchaseOrderId: grn.purchaseOrder?.id || '',
      remarks: grn.remarks || '',
      status: grn.status
    });
    
    // Fetch unused WBRs (exclude this GRN so its existing links stay selectable)
    const unusedWbrs = await fetchUnusedWeighBridgeReceipts(grn.id);
    if (weighBridgeReceipts.length === 0) {
      await fetchWeighBridgeReceipts();
    }
    
    // Load received/pending quantities if PO exists (for display purposes only)
    let receivedQuantitiesMap = {};
    if (grn.purchaseOrder?.id) {
      try {
        const response = await axios.get(`/api/inventory/purchase-orders/${grn.purchaseOrder.id}/line-items-with-received`);
        const { lineItems } = response.data;
        if (lineItems) {
          lineItems.forEach(poLineItem => {
            if (poLineItem.item?.id) {
              receivedQuantitiesMap[poLineItem.item.id] = {
                orderedQuantity: poLineItem.orderedQuantity || poLineItem.quantity || '',
                receivedQuantityTotal: poLineItem.receivedQuantity || '0',
                pendingQuantity: poLineItem.pendingQuantity || '',
                uomSymbol: poLineItem.unitOfMeasurement?.symbol || poLineItem.unitOfMeasurement?.name || ''
              };
            }
          });
        }
      } catch (error) {
        console.error('Error loading PO line items for edit:', error);
      }
    }
    
    // Separate material and service line items from GRN
    const materialLineItems = grn.lineItems?.filter(li => !li.lineType || li.lineType === 'MATERIAL') || [];
    const serviceLineItems = grn.lineItems?.filter(li => li.lineType === 'SERVICE') || [];
    
    // Map service items from GRN line items
    const serviceItemsData = serviceLineItems.map((li, serviceIdx) => {
      // Find the index of the linked material line item
      let linkedMaterialLineItemIndex = '';
      if (li.linkedMaterialLineItem?.id) {
        // Find the material line item index by matching the linked item's item ID
        const linkedItemId = li.linkedMaterialLineItem.item?.id;
        if (linkedItemId) {
          const materialIndex = materialLineItems.findIndex(mli => mli.item?.id === linkedItemId);
          if (materialIndex >= 0) {
            linkedMaterialLineItemIndex = materialIndex.toString();
          }
        }
      }
      
      return {
        id: li.id,
        serviceId: li.service?.id || '',
        serviceName: li.service?.name || '',
        quantity: li.receivedQuantity || 0,
        unitPrice: li.unitPrice || 0,
        lineTotal: li.lineTotal || 0,
        linkedMaterialItemId: li.linkedMaterialLineItem?.item?.id || null,
        linkedMaterialLineItemId: li.linkedMaterialLineItem?.id || null,
        linkedMaterialLineItemIndex: linkedMaterialLineItemIndex,
        vendor: li.vendor || null,
        description: '',
        vendorLotNumber: li.vendorLotNumber || '',
        internalLotNumber: li.internalLotNumber || ''
      };
    });
    
    // Set service items
    setGrnServiceItems(serviceItemsData);
    
    // Map material line items only
    const mappedLineItems = materialLineItems.map(li => {
      const quantity = parseFloat(li.receivedQuantity) || 0;
      const price = parseFloat(li.unitPrice) || 0;
      const total = (quantity * price).toFixed(2);
      const itemId = li.item?.id;
      const poData = receivedQuantitiesMap[itemId] || {};
      
      // Find weigh bridge receipt by RST number if it exists
      let weighBridgeReceiptId = '';
      if (li.weighBridgeReceiptNo) {
        const wbr = unusedWbrs.find(r => r.rstNumber === li.weighBridgeReceiptNo)
          || weighBridgeReceipts.find(r => r.rstNumber === li.weighBridgeReceiptNo);
        if (wbr) {
          weighBridgeReceiptId = wbr.id.toString();
        }
      }
      
      return {
        itemId: itemId || '',
        vendorId: li.vendor?.id || '',
        unitOfMeasurementId: li.unitOfMeasurement?.id || '',
        receivedQuantity: li.receivedQuantity || '',
        unitPrice: li.unitPrice || '',
        lineTotal: total,
        weighBridgeReceiptNo: li.weighBridgeReceiptNo || '',
        weighBridgeReceiptId: weighBridgeReceiptId,
        vendorLotNumber: li.vendorLotNumber || '',
        internalLotNumber: li.internalLotNumber || '',
        orderedQuantity: poData.orderedQuantity || '',
        receivedQuantityTotal: poData.receivedQuantityTotal || '',
        pendingQuantity: poData.pendingQuantity || '',
        uomSymbol: poData.uomSymbol || '',
        isFromPO: !!grn.purchaseOrder?.id,
        additionalCosts: li.additionalCosts ? li.additionalCosts.toString() : '',
        allocatedServiceCost: li.allocatedServiceCost ? li.allocatedServiceCost.toString() : '0',
        landedCost: li.landedCost ? li.landedCost.toString() : '0',
        landedCostPerUnit: li.landedCostPerUnit ? li.landedCostPerUnit.toString() : '0'
      };
    }) || [{ itemId: '', vendorId: '', unitOfMeasurementId: '', receivedQuantity: '', unitPrice: '', lineTotal: '', weighBridgeReceiptNo: '', weighBridgeReceiptId: '', vendorLotNumber: '', internalLotNumber: '', orderedQuantity: '', receivedQuantityTotal: '', pendingQuantity: '', uomSymbol: '', isFromPO: false, additionalCosts: '', allocatedServiceCost: 0, landedCost: 0, landedCostPerUnit: 0 }];
    
    // Calculate service costs and apply to material items
    const withServiceCosts = calculateServiceCosts(mappedLineItems, serviceItemsData);
    setGrnLineItems(withServiceCosts);
    
    setSelectedGRN(grn);
    setShowGRNModal(true);
  };

  const handleDeleteGRN = async (id) => {
    if (!window.confirm('Are you sure you want to delete this GRN?')) {
      return;
    }
    try {
      await axios.delete(`/api/inventory/grns/${id}`);
      toast.success('GRN deleted successfully');
      fetchGRNs();
    } catch (error) {
      const errorMsg = error.response?.data || 'Error deleting GRN';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Error deleting GRN');
    }
  };

  // Generate internal lot number in YYYYMMDD-SEQ format
  const generateInternalLotNumber = (existingItems = []) => {
    const now = new Date();
    const yyyy = String(now.getFullYear());
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    const datePrefix = `${yyyy}${mm}${dd}`;
    
    // Get sequence number for today - find highest existing sequence for today
    const todayPrefix = datePrefix;
    const itemsToCheck = existingItems.length > 0 ? existingItems : grnLineItems;
    const existingLotNumbers = itemsToCheck
      .map(item => item.internalLotNumber)
      .filter(lot => lot && lot.startsWith(todayPrefix + '-'))
      .map(lot => {
        const seqPart = lot.split('-')[1];
        return seqPart ? parseInt(seqPart) : 0;
      });
    
    const maxSeq = existingLotNumbers.length > 0 ? Math.max(...existingLotNumbers) : 0;
    const nextSeq = maxSeq + 1;
    
    return `${datePrefix}-${String(nextSeq).padStart(3, '0')}`;
  };

  const handleAddGRNLineItem = () => {
    const newInternalLotNumber = generateInternalLotNumber();
    setGrnLineItems([...grnLineItems, { itemId: '', vendorId: '', unitOfMeasurementId: '', receivedQuantity: '', unitPrice: '', lineTotal: '', weighBridgeReceiptNo: '', weighBridgeReceiptId: '', vendorLotNumber: '', internalLotNumber: newInternalLotNumber, orderedQuantity: '', receivedQuantityTotal: '', pendingQuantity: '', uomSymbol: '', isFromPO: false, additionalCosts: '', allocatedServiceCost: 0, landedCost: 0, landedCostPerUnit: 0 }]);
  };

  const handlePurchaseOrderChange = async (poId) => {
    setGrnFormData({ ...grnFormData, purchaseOrderId: poId });
    
    if (!poId) {
      // Clear line items if no PO selected
      const newInternalLotNumber = generateInternalLotNumber();
      setGrnLineItems([{ itemId: '', vendorId: '', unitOfMeasurementId: '', receivedQuantity: '', unitPrice: '', lineTotal: '', weighBridgeReceiptNo: '', weighBridgeReceiptId: '', vendorLotNumber: '', internalLotNumber: newInternalLotNumber, orderedQuantity: '', receivedQuantityTotal: '', pendingQuantity: '', uomSymbol: '', isFromPO: false, additionalCosts: '', allocatedServiceCost: 0, landedCost: 0, landedCostPerUnit: 0 }]);
      setGrnServiceItems([]);
      return;
    }
    
    try {
      // Fetch PO line items with received/pending quantities
      const response = await axios.get(`/api/inventory/purchase-orders/${poId}/line-items-with-received`);
      const { lineItems, serviceItems } = response.data;
      
      if (lineItems && lineItems.length > 0) {
        // Auto-populate GRN line items from PO line items
        const newLineItems = [];
        lineItems.forEach(poLineItem => {
          // Format unit price to 2 decimal places if it exists
          let formattedUnitPrice = '';
          if (poLineItem.unitPrice != null && poLineItem.unitPrice !== '') {
            const price = parseFloat(poLineItem.unitPrice);
            if (!isNaN(price)) {
              formattedUnitPrice = price.toFixed(2);
            }
          }
          
          // Filter out H&T and Transporter vendors (GRN line items cannot have these vendor types)
          let vendorId = '';
          if (poLineItem.vendor?.id) {
            // Check vendor type directly from PO response
            if (poLineItem.vendor.vendorType) {
              const vendorTypeName = poLineItem.vendor.vendorType.name.toUpperCase();
              // Only include vendor if it's NOT H&T or Transporter
              if (!vendorTypeName.includes('H&T') && !vendorTypeName.includes('TRANSPORTER')) {
                vendorId = poLineItem.vendor.id;
              }
            } else {
              // Include vendor if it has no vendor type
              vendorId = poLineItem.vendor.id;
            }
          }
          
          // Generate lot number based on items created so far
          const newInternalLotNumber = generateInternalLotNumber(newLineItems);
          newLineItems.push({
            itemId: poLineItem.item?.id || '',
            vendorId: vendorId,
            unitOfMeasurementId: poLineItem.unitOfMeasurement?.id || '',
            receivedQuantity: '', // User will enter the quantity being received now
            unitPrice: formattedUnitPrice,
            lineTotal: '',
            serviceCostAdjustment: 0, // Will be calculated from service items
            weighBridgeReceiptNo: '',
            weighBridgeReceiptId: '',
            vendorLotNumber: '',
            internalLotNumber: newInternalLotNumber,
            orderedQuantity: poLineItem.orderedQuantity || poLineItem.quantity || '',
            receivedQuantityTotal: poLineItem.receivedQuantity || '0',
            pendingQuantity: poLineItem.pendingQuantity || '',
            uomSymbol: poLineItem.unitOfMeasurement?.symbol || poLineItem.unitOfMeasurement?.name || '',
            isFromPO: true // Mark as from PO
          });
        });
        
        setGrnLineItems(newLineItems);
        
        // Store service items
        const newServiceItems = (serviceItems || []).map(serviceItem => {
          const unitPrice = parseFloat(serviceItem.unitPrice || 0);
          
          // If service is linked to a material item from PO, find the corresponding material line item
          let quantity = parseFloat(serviceItem.quantity || 0);
          let linkedMaterialLineItemIndex = '';
          
          if (serviceItem.linkedMaterialItemId) {
            // Find the material line item index by matching itemId (handle both string and number)
            const serviceItemId = String(serviceItem.linkedMaterialItemId);
            const materialIndex = newLineItems.findIndex(li => {
              if (!li.itemId) return false;
              return String(li.itemId) === serviceItemId;
            });
            if (materialIndex >= 0) {
              linkedMaterialLineItemIndex = materialIndex.toString();
              // For linked services, quantity will be set from material item's received quantity
              // We'll keep the PO quantity as initial value, but it will update when material quantity is entered
            }
          }
          
          const lineTotal = parseFloat(serviceItem.lineTotal || 0) || (quantity * unitPrice);
          
          return {
            id: serviceItem.id,
            serviceId: serviceItem.service?.id ? String(serviceItem.service.id) : '',
            serviceName: serviceItem.service?.name || '',
            quantity: quantity,
            unitPrice: unitPrice,
            lineTotal: lineTotal,
            linkedMaterialItemId: serviceItem.linkedMaterialItemId || null,
            linkedMaterialLineItemId: serviceItem.linkedMaterialLineItemId || null,
            linkedMaterialLineItemIndex: linkedMaterialLineItemIndex,
            vendor: serviceItem.vendor || null,
            description: serviceItem.description || '',
            remarks: serviceItem.remarks || ''
          };
        });
        
        setGrnServiceItems(newServiceItems);
        
        // Calculate service costs and apply to material items
        const withServiceCosts = calculateServiceCosts(newLineItems, newServiceItems);
        setGrnLineItems(withServiceCosts);
        
        setGrnFormData({ ...grnFormData, purchaseOrderId: poId });
      } else {
        toast.info('No material items with pending quantity found in this Purchase Order');
        const newInternalLotNumber = generateInternalLotNumber();
        setGrnLineItems([{ itemId: '', vendorId: '', unitOfMeasurementId: '', receivedQuantity: '', unitPrice: '', lineTotal: '', weighBridgeReceiptNo: '', weighBridgeReceiptId: '', vendorLotNumber: '', internalLotNumber: newInternalLotNumber, orderedQuantity: '', receivedQuantityTotal: '', pendingQuantity: '', uomSymbol: '', isFromPO: false, additionalCosts: '', allocatedServiceCost: 0, landedCost: 0, landedCostPerUnit: 0 }]);
        setGrnServiceItems([]);
      }
    } catch (error) {
      console.error('Error loading PO line items:', error);
      toast.error('Failed to load Purchase Order line items');
    }
  };

  // Calculate and apply service costs to material items
  const calculateServiceCosts = (lineItems, serviceItems) => {
    if (!serviceItems || serviceItems.length === 0) {
      return lineItems.map(li => {
        // Calculate base total from quantity * price
        const baseTotal = (parseFloat(li.receivedQuantity) || 0) * (parseFloat(li.unitPrice) || 0);
        return { ...li, serviceCostAdjustment: 0, lineTotal: baseTotal.toFixed(2) };
      });
    }
    
    const updated = lineItems.map(li => {
      // Calculate base total from quantity * price (not from existing lineTotal which may include service costs)
      const baseTotal = (parseFloat(li.receivedQuantity) || 0) * (parseFloat(li.unitPrice) || 0);
      return { ...li, serviceCostAdjustment: 0, lineTotal: baseTotal.toFixed(2) };
    });
    
    // Separate linked and unlinked services
    const linkedServices = serviceItems.filter(si => si.linkedMaterialItemId);
    const unlinkedServices = serviceItems.filter(si => !si.linkedMaterialItemId);
    
    // Apply linked service costs to specific material items
    linkedServices.forEach(service => {
      const materialItemIndex = updated.findIndex(li => li.itemId === service.linkedMaterialItemId.toString());
      if (materialItemIndex >= 0) {
        const materialItem = updated[materialItemIndex];
        const receivedQty = parseFloat(materialItem.receivedQuantity) || 0;
        const orderedQty = parseFloat(materialItem.orderedQuantity) || 1;
        
        if (orderedQty > 0 && receivedQty > 0) {
          // Calculate cost based on received quantity ratio
          const ratio = receivedQty / orderedQty;
          const serviceCost = (parseFloat(service.lineTotal) || 0) * ratio;
          updated[materialItemIndex].serviceCostAdjustment = 
            (parseFloat(updated[materialItemIndex].serviceCostAdjustment) || 0) + serviceCost;
        }
      }
    });
    
    // Distribute unlinked service costs across all material items based on quantity
    // First calculate total ordered quantity to determine proportion of PO received in this GRN
    if (unlinkedServices.length > 0) {
      const totalReceivedQty = updated.reduce((sum, li) => {
        return sum + (parseFloat(li.receivedQuantity) || 0);
      }, 0);
      
      const totalOrderedQty = updated.reduce((sum, li) => {
        return sum + (parseFloat(li.orderedQuantity) || 0);
      }, 0);
      
      if (totalReceivedQty > 0 && totalOrderedQty > 0) {
        // Calculate proportion: how much of the PO is received in this GRN
        const grnProportion = totalReceivedQty / totalOrderedQty;
        
        // Calculate total unlinked service cost from PO
        const totalUnlinkedServiceCostFromPO = unlinkedServices.reduce((sum, si) => {
          return sum + (parseFloat(si.lineTotal) || 0);
        }, 0);
        
        // Apply proportion to get service cost for this GRN
        const serviceCostForThisGRN = totalUnlinkedServiceCostFromPO * grnProportion;
        
        // Now distribute this GRN's service cost proportionally across material items in this GRN
        updated.forEach((li, index) => {
          const receivedQty = parseFloat(li.receivedQuantity) || 0;
          if (receivedQty > 0) {
            const itemProportion = receivedQty / totalReceivedQty;
            const allocatedCost = serviceCostForThisGRN * itemProportion;
            updated[index].serviceCostAdjustment = 
              (parseFloat(updated[index].serviceCostAdjustment) || 0) + allocatedCost;
          }
        });
      }
    }
    
    // Update line totals with service cost adjustments and calculate landed cost
    updated.forEach((li, index) => {
      const baseTotal = (parseFloat(li.receivedQuantity) || 0) * (parseFloat(li.unitPrice) || 0);
      const serviceCost = parseFloat(li.serviceCostAdjustment) || 0;
      updated[index].lineTotal = (baseTotal + serviceCost).toFixed(2);
      
      // Update allocated service cost (for landed cost calculation)
      updated[index].allocatedServiceCost = serviceCost.toFixed(2);
      
      // Calculate landed cost = base cost + service cost + additional costs
      const additional = parseFloat(li.additionalCosts) || 0;
      const landedCost = baseTotal + serviceCost + additional;
      updated[index].landedCost = landedCost.toFixed(2);
      
      // Calculate landed cost per unit
      const quantity = parseFloat(li.receivedQuantity) || 0;
      if (quantity > 0) {
        updated[index].landedCostPerUnit = (landedCost / quantity).toFixed(4);
      } else {
        updated[index].landedCostPerUnit = '0.0000';
      }
    });
    
    return updated;
  };

  const handleRemoveGRNLineItem = (index) => {
    setGrnLineItems(grnLineItems.filter((_, i) => i !== index));
  };

  const handleGRNLineItemChange = (index, field, value) => {
    const updated = [...grnLineItems];
    updated[index] = { ...updated[index], [field]: value };
    
    // Calculate line total when quantity or unit price changes
    if (field === 'receivedQuantity' || field === 'unitPrice') {
      const quantity = parseFloat(updated[index].receivedQuantity) || 0;
      const price = parseFloat(updated[index].unitPrice) || 0;
      updated[index].lineTotal = (quantity * price).toFixed(2);
    }
    
    // Calculate landed cost when base cost, service cost, or additional costs change
    if (field === 'lineTotal' || field === 'allocatedServiceCost' || field === 'additionalCosts' || field === 'receivedQuantity') {
      const baseCost = parseFloat(updated[index].lineTotal) || 0;
      const serviceCost = parseFloat(updated[index].allocatedServiceCost) || 0;
      const additional = parseFloat(updated[index].additionalCosts) || 0;
      const landedCost = baseCost + serviceCost + additional;
      updated[index].landedCost = landedCost.toFixed(2);
      
      // Calculate landed cost per unit
      const quantity = parseFloat(updated[index].receivedQuantity) || 0;
      if (quantity > 0) {
        updated[index].landedCostPerUnit = (landedCost / quantity).toFixed(4);
      } else {
        updated[index].landedCostPerUnit = '0.0000';
      }
    }
    
    // Update UOM symbol when UOM is changed
    if (field === 'unitOfMeasurementId') {
      const selectedUom = uoms.find(uom => uom.id === parseInt(value));
      updated[index].uomSymbol = selectedUom ? (selectedUom.symbol || selectedUom.name) : '';
    }
    
    // When weigh bridge receipt is selected, auto-populate quantity from netWeight
    if (field === 'weighBridgeReceiptId') {
      if (!value) {
        updated[index].weighBridgeReceiptNo = '';
      } else {
        const selectedReceipt = unusedWeighBridgeReceipts.find(r => r.id === parseInt(value))
          || weighBridgeReceipts.find(r => r.id === parseInt(value));
        if (selectedReceipt) {
          updated[index].weighBridgeReceiptNo = selectedReceipt.rstNumber;
          if (selectedReceipt.netWeight) {
            const newQuantity = parseFloat(selectedReceipt.netWeight).toFixed(2);
            updated[index].receivedQuantity = newQuantity;
            // Recalculate line total
            const quantity = parseFloat(updated[index].receivedQuantity) || 0;
            const price = parseFloat(updated[index].unitPrice) || 0;
            updated[index].lineTotal = (quantity * price).toFixed(2);
        
            // Update linked service quantities when weigh bridge receipt sets material quantity
            const materialItemId = updated[index].itemId;
            if (materialItemId) {
              const updatedServiceItems = grnServiceItems.map(si => {
                // Find the linked material index - check both linkedMaterialLineItemIndex and linkedMaterialItemId
                let linkedMaterialIndex = null;
                if (si.linkedMaterialLineItemIndex !== '' && si.linkedMaterialLineItemIndex !== null && si.linkedMaterialLineItemIndex !== undefined) {
                  linkedMaterialIndex = parseInt(si.linkedMaterialLineItemIndex);
                } else if (si.linkedMaterialItemId) {
                  // Find the material line item by matching itemId
                  linkedMaterialIndex = updated.findIndex(li => li.itemId && li.itemId.toString() === si.linkedMaterialItemId.toString());
                }
            
                // Check if this service is linked to the changed material item (by index or by itemId)
                const isLinkedToThisMaterial = linkedMaterialIndex === index || 
                  (si.linkedMaterialItemId && materialItemId.toString() === si.linkedMaterialItemId.toString());
            
                if (isLinkedToThisMaterial) {
                  const updatedQuantity = parseFloat(newQuantity);
                  const unitPrice = parseFloat(si.unitPrice) || 0;
                  const newLineTotal = updatedQuantity * unitPrice;
                  return {
                    ...si,
                    quantity: updatedQuantity,
                    lineTotal: newLineTotal
                  };
                }
                return si;
              });
              setGrnServiceItems(updatedServiceItems);
            }
          }
        }
      }
    }
    
    // If material quantity changed, update linked service quantities
    if (field === 'receivedQuantity') {
      const materialItemId = updated[index].itemId;
      if (materialItemId) {
        const newQuantity = parseFloat(value) || 0;
        // Update all service items linked to this material item
        const updatedServiceItems = grnServiceItems.map(si => {
          // Find the linked material index - check both linkedMaterialLineItemIndex and linkedMaterialItemId
          let linkedMaterialIndex = null;
          if (si.linkedMaterialLineItemIndex !== '' && si.linkedMaterialLineItemIndex !== null && si.linkedMaterialLineItemIndex !== undefined) {
            linkedMaterialIndex = parseInt(si.linkedMaterialLineItemIndex);
          } else if (si.linkedMaterialItemId) {
            // Find the material line item by matching itemId
            linkedMaterialIndex = updated.findIndex(li => li.itemId && li.itemId.toString() === si.linkedMaterialItemId.toString());
          }
          
          // Check if this service is linked to the changed material item (by index or by itemId)
          const isLinkedToThisMaterial = linkedMaterialIndex === index || 
            (si.linkedMaterialItemId && materialItemId.toString() === si.linkedMaterialItemId.toString());
          
          if (isLinkedToThisMaterial) {
            const updatedQuantity = newQuantity;
            const unitPrice = parseFloat(si.unitPrice) || 0;
            const newLineTotal = updatedQuantity * unitPrice;
            return {
              ...si,
              quantity: updatedQuantity,
              lineTotal: newLineTotal
            };
          }
          return si;
        });
        setGrnServiceItems(updatedServiceItems);
      }
    }
    
    // Recalculate service costs whenever quantity changes
    const withServiceCosts = calculateServiceCosts(updated, grnServiceItems);
    setGrnLineItems(withServiceCosts);
  };

  const handleSaveGRN = async (e) => {
    e.preventDefault();
    try {
      // Material line items
      const materialLineItems = grnLineItems
        .filter(li => li.itemId && li.receivedQuantity)
        .map(li => {
          // Filter out H&T and Transporter vendors before sending to backend
          let vendorRef = null;
          if (li.vendorId) {
            // Find vendor in vendors list to check its type
            const vendor = vendors.find(v => v.id === parseInt(li.vendorId));
            if (vendor) {
              if (vendor.vendorType) {
                const vendorTypeName = vendor.vendorType.name.toUpperCase();
                // Only include vendor if it's NOT H&T or Transporter
                if (!vendorTypeName.includes('H&T') && !vendorTypeName.includes('TRANSPORTER')) {
                  vendorRef = { id: parseInt(li.vendorId) };
                }
              } else {
                // Include vendor if it has no vendor type
                vendorRef = { id: parseInt(li.vendorId) };
              }
            }
          }
          
          return {
            lineType: 'MATERIAL',
            item: { id: parseInt(li.itemId) },
            service: null,
            vendor: vendorRef,
            unitOfMeasurement: li.unitOfMeasurementId ? { id: parseInt(li.unitOfMeasurementId) } : null,
            receivedQuantity: parseFloat(li.receivedQuantity),
            unitPrice: li.unitPrice ? parseFloat(li.unitPrice) : null,
            lineTotal: li.lineTotal ? parseFloat(li.lineTotal) : null,
            weighBridgeReceiptNo: li.weighBridgeReceiptNo || null,
            vendorLotNumber: li.vendorLotNumber || null,
            internalLotNumber: li.internalLotNumber || null,
            additionalCosts: li.additionalCosts ? parseFloat(li.additionalCosts) : null,
            allocatedServiceCost: li.allocatedServiceCost ? parseFloat(li.allocatedServiceCost) : null,
            landedCost: li.landedCost ? parseFloat(li.landedCost) : null,
            landedCostPerUnit: li.landedCostPerUnit ? parseFloat(li.landedCostPerUnit) : null
          };
        });

      // Service line items - include all service items that have a serviceId and quantity > 0
      const serviceLineItems = grnServiceItems
        .filter(si => {
          // Check if serviceId exists and is valid
          const serviceId = si.serviceId;
          const hasServiceId = serviceId !== null && serviceId !== undefined && serviceId !== '';
          
          // For linked services, check material quantity; for non-linked, check service quantity
          let linkedMaterialIndex = null;
          if (si.linkedMaterialLineItemIndex !== '' && si.linkedMaterialLineItemIndex !== null && si.linkedMaterialLineItemIndex !== undefined) {
            linkedMaterialIndex = parseInt(si.linkedMaterialLineItemIndex);
          } else if (si.linkedMaterialItemId) {
            linkedMaterialIndex = materialLineItems.findIndex(li => li.item?.id === parseInt(si.linkedMaterialItemId));
          }
          
          let quantity;
          if (linkedMaterialIndex !== null && linkedMaterialIndex >= 0 && linkedMaterialIndex < materialLineItems.length) {
            // Linked service: use material quantity
            quantity = parseFloat(materialLineItems[linkedMaterialIndex].receivedQuantity || 0);
          } else {
            // Non-linked service: use service quantity
            quantity = parseFloat(si.quantity || 0);
          }
          
          const hasQuantity = !isNaN(quantity) && quantity > 0;
          
          if (!hasServiceId) {
            console.warn('Service item missing serviceId:', si);
          }
          if (!hasQuantity) {
            console.warn('Service item has invalid quantity:', si, 'quantity:', quantity);
          }
          
          return hasServiceId && hasQuantity;
        })
        .map((si, serviceIndex) => {
          // Determine linked material line item index
          // Priority: 1) linkedMaterialLineItemIndex (user selection in GRN), 2) linkedMaterialItemId (from PO)
          let linkedMaterialIndex = null;
          if (si.linkedMaterialLineItemIndex !== '' && si.linkedMaterialLineItemIndex !== null && si.linkedMaterialLineItemIndex !== undefined) {
            // User selected a material item in GRN by index
            const materialIndex = parseInt(si.linkedMaterialLineItemIndex);
            if (materialIndex >= 0 && materialIndex < materialLineItems.length) {
              linkedMaterialIndex = materialIndex;
            }
          } else if (si.linkedMaterialItemId) {
            // Try to find the material line item by itemId
            const materialIndex = materialLineItems.findIndex(li => li.item?.id === parseInt(si.linkedMaterialItemId));
            if (materialIndex >= 0) {
              linkedMaterialIndex = materialIndex;
            }
          }
          
          // For linked services, use the material item's received quantity
          // For non-linked services, use the service item's quantity
          let quantity;
          if (linkedMaterialIndex !== null && linkedMaterialIndex >= 0 && linkedMaterialIndex < materialLineItems.length) {
            // Linked service: use material quantity
            quantity = parseFloat(materialLineItems[linkedMaterialIndex].receivedQuantity || 0);
          } else {
            // Non-linked service: use service item quantity
            quantity = parseFloat(si.quantity || 0);
          }
          
          const unitPrice = si.unitPrice ? parseFloat(si.unitPrice) : null;
          // Calculate line total based on the quantity
          const lineTotal = quantity * (unitPrice || 0);
          
          // For linking, if we have a material index, we'll send the material line item reference
          // For new GRNs, we'll use a temporary reference that the backend can resolve
          let linkedMaterialLineItemRef = null;
          if (linkedMaterialIndex !== null && linkedMaterialIndex >= 0 && linkedMaterialIndex < materialLineItems.length) {
            // Send a reference to the material line item by including it in the linkedMaterialLineItem
            // The backend will resolve this after saving
            // For now, we'll send null and handle it via metadata
            linkedMaterialLineItemRef = null; // Will be resolved via metadata
          }
          
          return {
            lineType: 'SERVICE',
            item: null,
            service: { id: parseInt(si.serviceId) },
            vendor: si.vendor?.id ? { id: parseInt(si.vendor.id) } : null,
            unitOfMeasurement: null, // Services may not have UOM
            receivedQuantity: quantity,
            unitPrice: unitPrice,
            lineTotal: lineTotal > 0 ? lineTotal : null,
            weighBridgeReceiptNo: null,
            remarks: si.remarks || '',
            linkedMaterialLineItem: linkedMaterialLineItemRef
          };
        });

      // Combine material and service line items
      const lineItems = [...materialLineItems, ...serviceLineItems];

      const payload = {
        grnNumber: grnFormData.grnNumber || null,
        grnDate: grnFormData.grnDate,
        warehouse: { id: parseInt(grnFormData.warehouseId) },
        purchaseOrder: grnFormData.purchaseOrderId ? { id: parseInt(grnFormData.purchaseOrderId) } : null,
        remarks: grnFormData.remarks || null,
        status: grnFormData.status || 'DRAFT',
        lineItems
      };
      
      // Store service-to-material link mapping for backend processing
      // Map service items to their linked material item indices
      const serviceLinkMap = new Map();
      serviceLineItems.forEach((si, serviceIdx) => {
        if (si.linkedMaterialLineItemIndex !== null && si.linkedMaterialLineItemIndex !== undefined) {
          const materialIdx = parseInt(si.linkedMaterialLineItemIndex);
          if (materialIdx >= 0 && materialIdx < materialLineItems.length) {
            // Store: serviceIndex in combined array -> materialIndex in material items array
            serviceLinkMap.set(materialLineItems.length + serviceIdx, materialIdx);
          }
        }
      });
      
      // Add link metadata to payload for backend to process
      if (serviceLinkMap.size > 0) {
        const linkMetadata = Array.from(serviceLinkMap.entries()).map(([serviceIdx, materialIdx]) => ({
          serviceIndex: serviceIdx,
          materialIndex: materialIdx
        }));
        payload._serviceLinkMetadata = linkMetadata;
      }

      // Debug: Log payload to see what's being sent
      console.log('GRN Save - Service Items State:', grnServiceItems);
      console.log('GRN Save - Material items count:', materialLineItems.length);
      console.log('GRN Save - Service items count:', serviceLineItems.length);
      console.log('GRN Save - Total line items count:', lineItems.length);
      console.log('GRN Save Payload:', JSON.stringify(payload, null, 2));

      if (selectedGRN) {
        await axios.put(`/api/inventory/grns/${selectedGRN.id}`, payload);
        toast.success('GRN updated successfully');
      } else {
        await axios.post('/api/inventory/grns', payload);
        toast.success('GRN created successfully');
      }
      setShowGRNModal(false);
      fetchGRNs();
    } catch (error) {
      console.error('GRN Save Error:', error);
      console.error('GRN Save Error Response:', error.response?.data);
      const data = error.response?.data;
      const errorMsg = typeof data === 'string' && data ? data
        : data?.message || data?.error
        || (error.response ? 'Error saving GRN' : 'Could not reach the server, so the GRN was not saved. Please try again.');
      toast.error(errorMsg);
    }
  };

  const handleCreateGIN = () => {
    setGinFormData({
      ginNumber: '',
      ginDate: todayISO(),
      warehouseId: '',
      referenceNumber: '',
      customerName: '',
      department: '',
      remarks: '',
      status: 'DRAFT',
      lineItems: []
    });
    setGinLineItems([{ itemId: '', quantity: '', unitOfMeasurementId: '', issuedQuantity: '', remarks: '' }]);
    setSelectedGIN(null);
    setShowGINModal(true);
  };

  const handleEditGIN = (gin) => {
    setGinFormData({
      ginNumber: gin.ginNumber,
      ginDate: gin.ginDate,
      warehouseId: gin.warehouse?.id || '',
      referenceNumber: gin.referenceNumber || '',
      customerName: gin.customerName || '',
      department: gin.department || '',
      remarks: gin.remarks || '',
      status: gin.status
    });
    setGinLineItems(gin.lineItems?.map(li => ({
      itemId: li.item?.id || '',
      quantity: li.quantity || '',
      unitOfMeasurementId: li.unitOfMeasurement?.id || '',
      issuedQuantity: li.issuedQuantity || li.quantity || '',
      remarks: li.remarks || ''
    })) || [{ itemId: '', quantity: '', unitOfMeasurementId: '', issuedQuantity: '', remarks: '' }]);
    setSelectedGIN(gin);
    setShowGINModal(true);
  };

  const handleDeleteGIN = async (id) => {
    if (!window.confirm('Are you sure you want to delete this GIN?')) {
      return;
    }
    try {
      await axios.delete(`/api/inventory/gins/${id}`);
      toast.success('GIN deleted successfully');
      fetchGINs();
    } catch (error) {
      const errorMsg = error.response?.data || 'Error deleting GIN';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Error deleting GIN');
    }
  };

  const handleAddGINLineItem = () => {
    setGinLineItems([...ginLineItems, { itemId: '', quantity: '', unitOfMeasurementId: '', issuedQuantity: '', remarks: '' }]);
  };

  const handleRemoveGINLineItem = (index) => {
    setGinLineItems(ginLineItems.filter((_, i) => i !== index));
  };

  const handleGINLineItemChange = (index, field, value) => {
    const updated = [...ginLineItems];
    updated[index] = { ...updated[index], [field]: value };
    if (field === 'quantity' && !updated[index].issuedQuantity) {
      updated[index].issuedQuantity = value;
    }
    setGinLineItems(updated);
  };

  const handleSaveGIN = async (e) => {
    e.preventDefault();
    try {
      const lineItems = ginLineItems
        .filter(li => li.itemId && li.quantity)
        .map(li => ({
          item: { id: parseInt(li.itemId) },
          quantity: parseFloat(li.quantity),
          unitOfMeasurement: li.unitOfMeasurementId ? { id: parseInt(li.unitOfMeasurementId) } : null,
          issuedQuantity: li.issuedQuantity ? parseFloat(li.issuedQuantity) : parseFloat(li.quantity),
          remarks: li.remarks || ''
        }));

      const payload = {
        ...ginFormData,
        warehouse: { id: parseInt(ginFormData.warehouseId) },
        lineItems
      };

      if (selectedGIN) {
        await axios.put(`/api/inventory/gins/${selectedGIN.id}`, payload);
        toast.success('GIN updated successfully');
      } else {
        await axios.post('/api/inventory/gins', payload);
        toast.success('GIN created successfully');
      }
      setShowGINModal(false);
      fetchGINs();
    } catch (error) {
      const errorMsg = error.response?.data || 'Error saving GIN';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Error saving GIN');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      'DRAFT': 'secondary',
      'RECEIVED': 'info',
      'PARTIAL': 'warning',
      'COMPLETED': 'success',
      'REVERSED': 'dark',
      'CANCELLED': 'danger',
      'ISSUED': 'success'
    };
    return <Badge bg={badges[status] || 'secondary'}>{status}</Badge>;
  };

  const filteredStockLevels = stockLevels.filter(stock => 
    stockFilterWarehouse === 'ALL' || stock.warehouseId.toString() === stockFilterWarehouse
  );

  return (
    <Container fluid className="mt-4">
      {/* Alerts for Purchase (which ones, and for whom, is in alerts/alertRegistry.js) */}
      <ModuleAlerts module="purchase" section={activeTab} />
      <Row>
        <Col>
          <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-4">
            {/* Vendor Types Tab */}
            <Tab 
              eventKey="vendor-types" 
              title="Vendor Types"
            >
              <div className="d-flex justify-content-between align-items-center mb-3">
                <p className="text-muted mb-0">Manage vendor types for categorizing vendors</p>
                <Button variant="primary" onClick={handleCreateVendorType}>
                  + Create Vendor Type
                </Button>
              </div>

              {loading ? (
                <div className="d-flex justify-content-center">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                </div>
              ) : (
                <Card>
                  <Card.Body>
                    <Table striped bordered hover responsive>
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Description</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {vendorTypes.map((vendorType) => (
                          <tr key={vendorType.id}>
                            <td>{vendorType.name}</td>
                            <td>{vendorType.description || '-'}</td>
                            <td>
                              <Badge bg={vendorType.isActive ? 'success' : 'secondary'}>
                                {vendorType.isActive ? 'Active' : 'Inactive'}
                              </Badge>
                            </td>
                            <td>
                              <span title="View & Print" style={{ display: 'inline-block' }}>
                                <Button
                                  size="sm"
                                  variant="outline-info"
                                  className="me-2"
                                  onClick={() => handleViewPrint(vendorType, 'VENDOR_TYPE')}
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                                    <path d="M5 1a2 2 0 0 0-2 2v1h10V3a2 2 0 0 0-2-2H5zm6 8H5a1 1 0 0 0-1 1v3a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1v-3a1 1 0 0 0-1-1z"/>
                                    <path d="M0 7a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2h-1v-2a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v2H2a2 2 0 0 1-2-2V7zm2.5 1a.5.5 0 1 0 0-1 .5.5 0 0 0 0 1z"/>
                                  </svg>
                                </Button>
                              </span>
                              <span title="Edit" style={{ display: 'inline-block' }}>
                                <Button
                                  size="sm"
                                  variant="outline-primary"
                                  className="me-2"
                                  onClick={() => handleEditVendorType(vendorType)}
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                                    <path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z"/>
                                    <path fillRule="evenodd" d="M1 13.5A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-6a.5.5 0 0 0-1 0v6a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5H9a.5.5 0 0 0 0-1H2.5A1.5 1.5 0 0 0 1 2.5v11z"/>
                                  </svg>
                                </Button>
                              </span>
                              <span title="Delete" style={{ display: 'inline-block' }}>
                                <Button
                                  size="sm"
                                  variant="outline-danger"
                                  onClick={() => handleDeleteVendorType(vendorType.id)}
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                                    <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
                                    <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
                                  </svg>
                                </Button>
                              </span>
                            </td>
                          </tr>
                        ))}
                        {vendorTypes.length === 0 && (
                          <tr>
                            <td colSpan="4" className="text-center text-muted">
                              No vendor types found. Create one to get started.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              )}
            </Tab>

            {/* Vendors Tab */}
            <Tab 
              eventKey="vendors" 
              title="Vendors"
            >
              {showVendorTransactions ? (
                // Vendor Transactions View
                <div>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <div>
                      <Button variant="outline-secondary" onClick={handleBackToVendors} className="me-2">
                        ← Back to Vendors
                      </Button>
                      <h5 className="d-inline-block mb-0 ms-2">{selectedVendor?.name || 'Vendor'}</h5>
                    </div>
                  </div>
                  <div className="mb-3">
                    <Row>
                      <Col md={6}>
                        <p className="mb-1"><strong>Company:</strong> {selectedVendor?.companyName || '-'}</p>
                        <p className="mb-1"><strong>Contact Person:</strong> {selectedVendor?.contactPerson || '-'}</p>
                        <p className="mb-1"><strong>Email:</strong> {selectedVendor?.email || '-'}</p>
                      </Col>
                      <Col md={6}>
                        <p className="mb-1"><strong>Phone:</strong> {selectedVendor?.phoneNumber || '-'}</p>
                        <p className="mb-1"><strong>Vendor Type:</strong> {selectedVendor?.vendorType?.name || '-'}</p>
                        <p className="mb-1"><strong>Status:</strong> <Badge bg={selectedVendor?.isActive ? 'success' : 'secondary'}>{selectedVendor?.isActive ? 'Active' : 'Inactive'}</Badge></p>
                      </Col>
                    </Row>
                  </div>
                  <Card>
                    <Card.Body>
                      {loadingVendorTransactions ? (
                        <div className="d-flex justify-content-center">
                          <Spinner animation="border" role="status" />
                        </div>
                      ) : vendorTransactions.length === 0 ? (
                        <Alert variant="info">
                          No transactions found for this vendor.
                        </Alert>
                      ) : (
                        <Table striped bordered hover responsive>
                          <thead>
                            <tr>
                              <th>Date</th>
                              <th>Type</th>
                              <th>Reference</th>
                              <th>Description</th>
                              <th>Amount</th>
                              <th>Details</th>
                              <th>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {vendorTransactions.map((transaction) => {
                              const isPayment = transaction.type === 'PAYMENT';
                              const amount = parseFloat(transaction.amount) || 0;
                              const isNegative = amount < 0;
                              return (
                                <tr key={`${transaction.type}-${transaction.id}`}>
                                  <td>{formatDate(transaction.date)}</td>
                                  <td>
                                    <Badge bg={isPayment ? 'warning' : 'info'}>
                                      {transaction.type}
                                    </Badge>
                                  </td>
                                  <td>
                                    {transaction.reference ? (
                                      <button
                                        type="button"
                                        className="btn btn-link p-0 align-baseline text-decoration-underline"
                                        onClick={() => handleViewVendorTransactionDocument(transaction)}
                                        title="View reference document"
                                      >
                                        {transaction.reference}
                                      </button>
                                    ) : (
                                      '-'
                                    )}
                                  </td>
                                  <td>{transaction.description || '-'}</td>
                                  <td>
                                    <strong style={{ color: isNegative ? 'red' : 'green' }}>
                                      {isNegative ? '-' : '+'}{formatCurrency(Math.abs(amount))}
                                    </strong>
                                  </td>
                                  <td>
                                    {transaction.type === 'GRN' && transaction.warehouse && (
                                      <span className="text-muted small">Warehouse: {transaction.warehouse}</span>
                                    )}
                                    {transaction.type === 'PAYMENT' && (
                                      <div className="small">
                                        <div>Method: {transaction.paymentMethod || '-'}</div>
                                        {transaction.sourceAccount && (
                                          <div className="text-muted">Source: {transaction.sourceAccount}</div>
                                        )}
                                      </div>
                                    )}
                                  </td>
                                  <td>
                                    {transaction.status && (
                                      <Badge bg={transaction.status === 'RECORDED' || transaction.status === 'COMPLETED' ? 'success' : 'secondary'}>
                                        {transaction.status}
                                      </Badge>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </Table>
                      )}
                    </Card.Body>
                  </Card>
                </div>
              ) : (
                // Vendors List View
                <div>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <p className="text-muted mb-0">Manage vendor information and details</p>
                    <Button variant="primary" onClick={handleCreateVendor}>
                      + Create Vendor
                    </Button>
                  </div>

                  {loading ? (
                    <div className="d-flex justify-content-center">
                      <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </Spinner>
                    </div>
                  ) : (
                    <Card>
                      <Card.Body>
                        <Table striped bordered hover responsive>
                          <thead>
                            <tr>
                              <th>Name</th>
                              <th>Company</th>
                              <th>Contact Person</th>
                              <th>GSTIN</th>
                              <th>Email</th>
                              <th>Phone</th>
                              <th>Vendor Type</th>
                              <th>Status</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {vendors.map((vendor) => (
                              <tr key={vendor.id}>
                                <td>{vendor.name}</td>
                                <td>{vendor.companyName || '-'}</td>
                                <td>{vendor.contactPerson || '-'}</td>
                                <td>
                                  {vendor.taxId || '-'}
                                  {vendor.gstRegistrationType === 'COMPOSITION' && <Badge bg="secondary" className="ms-1">Composition</Badge>}
                                  {vendor.country && vendor.country.toLowerCase() === 'india' && !vendor.taxId && vendor.gstRegistrationType !== 'UNREGISTERED' && <Badge bg="warning" text="dark" className="ms-1">No GSTIN</Badge>}
                                </td>
                                <td>{vendor.email || '-'}</td>
                                <td>{vendor.phoneNumber || '-'}</td>
                                <td>{vendor.vendorType?.name || '-'}</td>
                                <td>
                                  <Badge bg={vendor.isActive ? 'success' : 'secondary'}>
                                    {vendor.isActive ? 'Active' : 'Inactive'}
                                  </Badge>
                                </td>
                                <td>
                                  <span title="View Transactions" style={{ display: 'inline-block' }}>
                                    <Button
                                      size="sm"
                                      variant="outline-info"
                                      className="me-2"
                                      onClick={() => handleViewVendorTransactions(vendor)}
                                    >
                                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                                        <path d="M1 11.5a.5.5 0 0 0 .5.5h11.793l-3.147 3.146a.5.5 0 0 0 .708.708l4-4a.5.5 0 0 0 0-.708l-4-4a.5.5 0 0 0-.708.708L13.293 11H1.5a.5.5 0 0 0-.5.5zm14-7a.5.5 0 0 1-.5.5H2.707l3.147 3.146a.5.5 0 1 1-.708.708l-4-4a.5.5 0 0 1 0-.708l4-4a.5.5 0 1 1 .708.708L2.707 4H14.5a.5.5 0 0 1 .5.5z"/>
                                      </svg>
                                    </Button>
                                  </span>
                                  <span title="View & Print" style={{ display: 'inline-block' }}>
                                    <Button
                                      size="sm"
                                      variant="outline-info"
                                      className="me-2"
                                      onClick={() => handleViewPrint(vendor, 'VENDOR')}
                                    >
                                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                                        <path d="M5 1a2 2 0 0 0-2 2v1h10V3a2 2 0 0 0-2-2H5zm6 8H5a1 1 0 0 0-1 1v3a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1v-3a1 1 0 0 0-1-1z"/>
                                        <path d="M0 7a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2h-1v-2a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v2H2a2 2 0 0 1-2-2V7zm2.5 1a.5.5 0 1 0 0-1 .5.5 0 0 0 0 1z"/>
                                      </svg>
                                    </Button>
                                  </span>
                                  <span title="Edit" style={{ display: 'inline-block' }}>
                                    <Button
                                      size="sm"
                                      variant="outline-primary"
                                      className="me-2"
                                      onClick={() => handleEditVendor(vendor)}
                                    >
                                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                                        <path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z"/>
                                        <path fillRule="evenodd" d="M1 13.5A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-6a.5.5 0 0 0-1 0v6a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5H9a.5.5 0 0 0 0-1H2.5A1.5 1.5 0 0 0 1 2.5v11z"/>
                                      </svg>
                                    </Button>
                                  </span>
                                  <span title="Delete" style={{ display: 'inline-block' }}>
                                    <Button
                                      size="sm"
                                      variant="outline-danger"
                                      onClick={() => handleDeleteVendor(vendor.id)}
                                    >
                                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                                        <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
                                        <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
                                      </svg>
                                    </Button>
                                  </span>
                                  <HistoryButton documentType="Vendor" documentId={vendor.id} label={vendor.name} className="ms-1" />
                                </td>
                              </tr>
                            ))}
                            {vendors.length === 0 && (
                              <tr>
                                <td colSpan="8" className="text-center text-muted">
                                  No vendors found. Create one to get started.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </Table>
                      </Card.Body>
                    </Card>
                  )}
                </div>
              )}
            </Tab>

            <Tab
              eventKey="procurement-surveys"
              title="Procurement Surveys"
            >
              <FeatureNotice feature="PROCUREMENT_SURVEYS" />
              {activeTab === 'procurement-surveys' && (
                <ProcurementSurveysTab setHelpContent={setHelpContent} setShowHelpPane={setShowHelpPane} />
              )}
            </Tab>

            {/* Purchase Orders Tab */}
            <Tab 
              eventKey="purchase-orders" 
              title="Purchase Orders"
            >
              <div className="d-flex justify-content-between align-items-center mb-3">
                <p className="text-muted mb-0">Create and manage multi-vendor purchase orders</p>
                <Button variant="primary" onClick={handleCreatePO}>
                  + Create Purchase Order
                </Button>
              </div>
              <LowStock active={activeTab === 'purchase-orders'} onMakePO={handleMakePOFromLowStock} reloadKey={purchaseOrders.length} />

              {loading ? (
                <div className="d-flex justify-content-center">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                </div>
              ) : (
                <Card>
                  <Card.Body>
                    <Table striped bordered hover responsive>
                      <thead>
                        <tr>
                          <th>PO Number</th>
                          <th>Date</th>
                          <th>Warehouse</th>
                          <th>Required Date</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {purchaseOrders.map((po) => (
                          <tr key={po.id}>
                            <td>{po.poNumber || '-'}</td>
                            <td>{formatDate(po.poDate)}</td>
                            <td>{po.warehouse?.name || '-'}</td>
                            <td>{formatDate(po.requiredDate) || '-'}</td>
                            <td>{getStatusBadge(po.status)}</td>
                            <td>
                              <span title="View & Print" style={{ display: 'inline-block' }}>
                                <Button
                                  size="sm"
                                  variant="outline-info"
                                  className="me-2"
                                  onClick={() => setPrintingPO(po)}
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                                    <path d="M5 1a2 2 0 0 0-2 2v1h10V3a2 2 0 0 0-2-2H5zm6 8H5a1 1 0 0 0-1 1v3a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1v-3a1 1 0 0 0-1-1z"/>
                                    <path d="M0 7a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2h-1v-2a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v2H2a2 2 0 0 1-2-2V7zm2.5 1a.5.5 0 1 0 0-1 .5.5 0 0 0 0 1z"/>
                                  </svg>
                                </Button>
                              </span>
                              {!['DRAFT', 'CANCELLED'].includes(po.status) && poVendors(po).length > 0 && (
                                <span title={poVendors(po).length > 1 ? 'Send to a vendor (choose which)' : `Send to ${poVendors(po)[0].name} on WhatsApp or by email`} style={{ display: 'inline-block' }}>
                                  <Button
                                    size="sm"
                                    variant="outline-success"
                                    className="me-2"
                                    onClick={() => (poVendors(po).length > 1 ? setPrintingPO(po) : setSendingPO({ po, vendor: poVendors(po)[0] }))}
                                  >
                                    <i className="bi bi-send" />
                                  </Button>
                                </span>
                              )}
                              <span title="View Transactions" style={{ display: 'inline-block' }}>
                                <Button
                                  size="sm"
                                  variant="outline-primary"
                                  className="me-2"
                                  onClick={() => handleViewPOTransactions(po)}
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                                    <path d="M1 11.5a.5.5 0 0 0 .5.5h11.793l-3.147 3.146a.5.5 0 0 0 .708.708l4-4a.5.5 0 0 0 0-.708l-4-4a.5.5 0 0 0-.708.708L13.293 11H1.5a.5.5 0 0 0-.5.5zm14-7a.5.5 0 0 1-.5.5H2.707l3.147 3.146a.5.5 0 1 1-.708.708l-4-4a.5.5 0 0 1 0-.708l4-4a.5.5 0 1 1 .708.708L2.707 4H14.5a.5.5 0 0 1 .5.5z"/>
                                  </svg>
                                </Button>
                              </span>
                              {(po.status === 'DRAFT' || po.status === 'APPROVED') && (
                                <span title="Edit" style={{ display: 'inline-block' }}>
                                  <Button
                                    size="sm"
                                    variant="outline-primary"
                                    className="me-2"
                                    onClick={() => handleEditPO(po)}
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                                      <path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z"/>
                                      <path fillRule="evenodd" d="M1 13.5A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-6a.5.5 0 0 0-1 0v6a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5H9a.5.5 0 0 0 0-1H2.5A1.5 1.5 0 0 0 1 2.5v11z"/>
                                    </svg>
                                  </Button>
                                </span>
                              )}
                              {po.status === 'DRAFT' && (
                                <>
                                  <span title="Approve" style={{ display: 'inline-block' }}>
                                    <Button
                                      size="sm"
                                      variant="outline-success"
                                      className="me-2"
                                      onClick={() => handleApprovePO(po)}
                                    >
                                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                                        <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
                                        <path d="M10.97 4.97a.75.75 0 0 1 1.07 1.05l-3.99 4.99a.75.75 0 0 1-1.08.02L4.324 8.384a.75.75 0 1 1 1.06-1.06l2.094 2.093 3.473-4.425a.267.267 0 0 1 .02-.022z"/>
                                      </svg>
                                    </Button>
                                  </span>
                                  <span title="Delete" style={{ display: 'inline-block' }}>
                                    <Button
                                      size="sm"
                                      variant="outline-danger"
                                      onClick={() => handleDeletePO(po.id)}
                                    >
                                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                                        <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
                                        <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
                                      </svg>
                                    </Button>
                                  </span>
                                </>
                              )}
                              <HistoryButton documentType="PurchaseOrder" documentId={po.id} label={`Purchase order ${po.poNumber || po.id}`} className="ms-1" />
                            </td>
                          </tr>
                        ))}
                        {purchaseOrders.length === 0 && (
                          <tr>
                            <td colSpan="6" className="text-center text-muted">
                              No purchase orders found. Create one to get started.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              )}
            </Tab>

            {/* Weigh Bridge Receipts Tab */}
            <Tab 
              eventKey="weigh-bridge-receipts" 
              title="WBR"
            >
              <FeatureNotice feature="WEIGHBRIDGE" />
              <div className="d-flex justify-content-between align-items-center mb-3">
                <p className="text-muted mb-0">Record vehicle weights at weigh bridge stations</p>
                <Button variant="primary" onClick={handleCreateWBR}>
                  + Create Receipt
                </Button>
              </div>

              {loading ? (
                <div className="d-flex justify-content-center">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                </div>
              ) : (
                <Card>
                  <Card.Body>
                    <Table striped bordered hover responsive>
                      <thead>
                        <tr>
                          <th>RST Number</th>
                          <th>Date</th>
                          <th>Time</th>
                          <th>Vendor</th>
                          <th>Material</th>
                          <th>Reference</th>
                          <th>Gross Weight</th>
                          <th>Empty Weight</th>
                          <th>Net Weight</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {weighBridgeReceipts.map((wbr) => {
                          const netWeight = (parseFloat(wbr.grossWeight) || 0) - (parseFloat(wbr.emptyWeight) || 0);
                          return (
                            <tr key={wbr.id}>
                              <td>{wbr.rstNumber}</td>
                              <td>{formatDate(wbr.receiptDate)}</td>
                              <td>{wbr.receiptTime || '-'}</td>
                              <td>{wbr.vendor?.name || '-'}</td>
                              <td>{wbr.material?.name || '-'}</td>
                              <td>
                                {wbr.procurementSurvey?.surveyNumber
                                  ? `PS: ${wbr.procurementSurvey.surveyNumber}`
                                  : wbr.purchaseOrder?.poNumber
                                    ? `PO: ${wbr.purchaseOrder.poNumber}`
                                    : '-'}
                              </td>
                              <td>{parseFloat(wbr.grossWeight || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {getWBRUOM(wbr)}</td>
                              <td>{parseFloat(wbr.emptyWeight || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {getWBRUOM(wbr)}</td>
                              <td><strong>{netWeight.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {getWBRUOM(wbr)}</strong></td>
                              <td>
                                <span title="View & Print" style={{ display: 'inline-block' }}>
                                  <Button
                                    size="sm"
                                    variant="outline-info"
                                    className="me-2"
                                    onClick={() => handleViewPrint(wbr, 'WBR')}
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                                      <path d="M5 1a2 2 0 0 0-2 2v1h10V3a2 2 0 0 0-2-2H5zm6 8H5a1 1 0 0 0-1 1v3a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1v-3a1 1 0 0 0-1-1z"/>
                                      <path d="M0 7a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2h-1v-2a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v2H2a2 2 0 0 1-2-2V7zm2.5 1a.5.5 0 1 0 0-1 .5.5 0 0 0 0 1z"/>
                                    </svg>
                                  </Button>
                                </span>
                                <span title="Edit" style={{ display: 'inline-block' }}>
                                  <Button
                                    size="sm"
                                    variant="outline-primary"
                                    className="me-2"
                                    onClick={() => handleEditWBR(wbr)}
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                                      <path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z"/>
                                      <path fillRule="evenodd" d="M1 13.5A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-6a.5.5 0 0 0-1 0v6a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5H9a.5.5 0 0 0 0-1H2.5A1.5 1.5 0 0 0 1 2.5v11z"/>
                                    </svg>
                                  </Button>
                                </span>
                                <span title="Delete" style={{ display: 'inline-block' }}>
                                  <Button
                                    size="sm"
                                    variant="outline-danger"
                                    onClick={() => handleDeleteWBR(wbr.id)}
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                                      <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
                                      <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
                                    </svg>
                                  </Button>
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                        {weighBridgeReceipts.length === 0 && (
                          <tr>
                            <td colSpan="10" className="text-center text-muted">
                              No weigh bridge receipts found. Create one to get started.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              )}
            </Tab>
          
            {/* Purchase Invoices Tab (vendor tax invoices, for input tax credit) */}
            <Tab
              eventKey="purchase-invoices"
              title="Purchase Invoices"
            >
              <PurchaseInvoices active={activeTab === 'purchase-invoices'} />
            </Tab>
            <Tab
              eventKey="non-gst-purchases"
              title="Non-GST Purchases"
            >
              <NonGstPurchases active={activeTab === 'non-gst-purchases'} />
            </Tab>
            {canSeeReports && (
              <Tab eventKey="reports" title="Reports">
                <PurchaseReports active={activeTab === 'reports'} currency={organizationCurrency} />
              </Tab>
            )}
          </Tabs>
        </Col>
      </Row>

      {/* GRN Modal */}
      <Modal show={showGRNModal} onHide={() => setShowGRNModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{selectedGRN ? 'Edit GRN' : 'Create GRN'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSaveGRN}>
          <Modal.Body>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>GRN Number</Form.Label>
                  <Form.Control
                    type="text"
                    value={grnFormData.grnNumber}
                    onChange={(e) => setGrnFormData({ ...grnFormData, grnNumber: e.target.value })}
                    placeholder="Auto-generated if left empty"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>GRN Date *</Form.Label>
                  <DateInput
                   
                    value={grnFormData.grnDate}
                    onChange={(e) => setGrnFormData({ ...grnFormData, grnDate: e.target.value })}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Warehouse *</Form.Label>
                  <Form.Select
                    value={grnFormData.warehouseId}
                    onChange={(e) => setGrnFormData({ ...grnFormData, warehouseId: e.target.value, purchaseOrderId: '' })}
                    required
                  >
                    <option value="">Select Warehouse</option>
                    {warehouses.filter(w => w.isActive).map(wh => (
                      <option key={wh.id} value={wh.id}>{wh.name}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Purchase Order</Form.Label>
                  <Form.Select
                    value={grnFormData.purchaseOrderId}
                    onChange={(e) => handlePurchaseOrderChange(e.target.value)}
                    disabled={!grnFormData.warehouseId}
                  >
                    <option value="">Select Purchase Order</option>
                    {purchaseOrders
                      .filter(po => !grnFormData.warehouseId || po.warehouse?.id === parseInt(grnFormData.warehouseId))
                      .map(po => (
                        <option key={po.id} value={po.id}>
                          {po.poNumber} {po.poDate ? `(${formatDate(po.poDate)})` : ''}
                        </option>
                      ))}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    {!grnFormData.warehouseId ? 'Please select a warehouse first' : 'Purchase orders filtered by selected warehouse'}
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    value={grnFormData.status}
                    onChange={(e) => setGrnFormData({ ...grnFormData, status: e.target.value })}
                  >
                    <option value="DRAFT">DRAFT</option>
                    <option value="PARTIAL">PARTIAL</option>
                    <option value="RECEIVED">RECEIVED</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Remarks</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={grnFormData.remarks}
                onChange={(e) => setGrnFormData({ ...grnFormData, remarks: e.target.value })}
              />
            </Form.Group>

            <div className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <Form.Label className="mb-0">Line Items *</Form.Label>
                <Button size="sm" variant="outline-primary" onClick={handleAddGRNLineItem}>
                  + Add Item
                </Button>
              </div>
              {grnLineItems.map((lineItem, index) => (
                <Card key={index} className="mb-2">
                  <Card.Body>
                    {(lineItem.orderedQuantity || lineItem.receivedQuantityTotal || lineItem.pendingQuantity) && (
                      <Alert variant="info" className="mb-2 py-2">
                        <small>
                          <strong>PO Summary:</strong> Ordered: {parseFloat(lineItem.orderedQuantity || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}{lineItem.uomSymbol ? ` ${lineItem.uomSymbol}` : ''} | 
                          Already Received: {parseFloat(lineItem.receivedQuantityTotal || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}{lineItem.uomSymbol ? ` ${lineItem.uomSymbol}` : ''} | 
                          Pending: {parseFloat(lineItem.pendingQuantity || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}{lineItem.uomSymbol ? ` ${lineItem.uomSymbol}` : ''}
                        </small>
                      </Alert>
                    )}
                    <Row>
                      <Col md={2}>
                        <Form.Group>
                          <Form.Label>Item *</Form.Label>
                          <Form.Select
                            value={lineItem.itemId}
                            onChange={(e) => handleGRNLineItemChange(index, 'itemId', e.target.value)}
                            required
                            disabled={lineItem.isFromPO} // Read-only for items from PO
                          >
                            <option value="">Select Item</option>
                            {items.filter(i => i.isActive).map(item => (
                              <option key={item.id} value={item.id}>{item.name}</option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={2}>
                        <Form.Group>
                          <Form.Label>Vendor</Form.Label>
                          <Form.Select
                            value={lineItem.vendorId}
                            onChange={(e) => handleGRNLineItemChange(index, 'vendorId', e.target.value)}
                            disabled={lineItem.isFromPO} // Read-only for items from PO
                          >
                            <option value="">Select Vendor</option>
                            {vendors
                              .filter(v => v.isActive && (!v.vendorType || 
                                (!v.vendorType.name.toUpperCase().includes('H&T') && 
                                 !v.vendorType.name.toUpperCase().includes('TRANSPORTER'))))
                              .map(vendor => (
                                <option key={vendor.id} value={vendor.id}>{vendor.name}</option>
                              ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={2}>
                        <Form.Group>
                          <Form.Label>UOM</Form.Label>
                          <Form.Select
                            value={lineItem.unitOfMeasurementId}
                            onChange={(e) => handleGRNLineItemChange(index, 'unitOfMeasurementId', e.target.value)}
                            disabled={lineItem.isFromPO} // Read-only for items from PO
                          >
                            <option value="">Select UOM</option>
                            {uoms.map(uom => (
                              <option key={uom.id} value={uom.id}>{uom.name} ({uom.symbol})</option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={2}>
                        <Form.Group>
                          <Form.Label>Received Quantity *</Form.Label>
                          <Form.Control
                            type="number"
                            step="0.01"
                            value={lineItem.receivedQuantity}
                            onChange={(e) => handleGRNLineItemChange(index, 'receivedQuantity', e.target.value)}
                            required
                            disabled={lineItem.isFromPO && lineItem.weighBridgeReceiptId} // Disabled if from PO and weigh bridge receipt is selected
                          />
                          {lineItem.pendingQuantity && (
                            <Form.Text className="text-muted">
                              Remaining: {parseFloat(lineItem.pendingQuantity).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}{lineItem.uomSymbol ? ` ${lineItem.uomSymbol}` : ''}
                            </Form.Text>
                          )}
                        </Form.Group>
                      </Col>
                      <Col md={2}>
                        <Form.Group>
                          <Form.Label>Unit Price</Form.Label>
                          <Form.Control
                            type="number"
                            step="0.01"
                            value={lineItem.unitPrice}
                            onChange={(e) => handleGRNLineItemChange(index, 'unitPrice', e.target.value)}
                            placeholder="0.00"
                            disabled={lineItem.isFromPO} // Read-only for items from PO
                          />
                        </Form.Group>
                      </Col>
                      <Col md={1}>
                        <Form.Group>
                          <Form.Label>Line Total</Form.Label>
                          <Form.Control
                            type="text"
                            value={lineItem.lineTotal || '0.00'}
                            readOnly
                            className="bg-body-secondary"
                            title={lineItem.serviceCostAdjustment > 0 ? `Base: ${(parseFloat(lineItem.lineTotal || 0) - parseFloat(lineItem.serviceCostAdjustment || 0)).toFixed(2)}, Service Cost: ${parseFloat(lineItem.serviceCostAdjustment || 0).toFixed(2)}` : ''}
                          />
                          {lineItem.serviceCostAdjustment > 0 && (
                            <Form.Text className="text-muted small">
                              +{parseFloat(lineItem.serviceCostAdjustment).toFixed(2)} service
                            </Form.Text>
                          )}
                        </Form.Group>
                      </Col>
                      <Col md={1}>
                        <Form.Group>
                          <Form.Label>Actions</Form.Label>
                          <Button
                            size="sm"
                            variant="outline-danger"
                            onClick={() => handleRemoveGRNLineItem(index)}
                            disabled={grnLineItems.length === 1}
                          >
                            Remove
                          </Button>
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={3}>
                        <Form.Group>
                          <Form.Label>Additional Costs</Form.Label>
                          <Form.Control
                            type="number"
                            step="0.01"
                            value={lineItem.additionalCosts || ''}
                            onChange={(e) => handleGRNLineItemChange(index, 'additionalCosts', e.target.value)}
                            placeholder="0.00"
                            title="Freight, insurance, customs, handling, etc."
                          />
                          <Form.Text className="text-muted small">
                            Freight, insurance, customs, etc.
                          </Form.Text>
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group>
                          <Form.Label>Allocated Service Cost</Form.Label>
                          <Form.Control
                            type="text"
                            value={parseFloat(lineItem.allocatedServiceCost || 0).toFixed(2)}
                            readOnly
                            className="bg-body-secondary"
                            title="Service costs allocated to this lot"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group>
                          <Form.Label>Landed Cost</Form.Label>
                          <Form.Control
                            type="text"
                            value={parseFloat(lineItem.landedCost || 0).toFixed(2)}
                            readOnly
                            className="bg-body-secondary fw-bold"
                            title="Total landed cost = Base cost + Service cost + Additional costs"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group>
                          <Form.Label>Landed Cost/Unit</Form.Label>
                          <Form.Control
                            type="text"
                            value={parseFloat(lineItem.landedCostPerUnit || 0).toFixed(4)}
                            readOnly
                            className="bg-body-secondary"
                            title="Landed cost per unit = Landed cost / Received quantity"
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>Weigh Bridge Receipt</Form.Label>
                          <Form.Select
                            value={lineItem.weighBridgeReceiptId || ''}
                            onChange={(e) => handleGRNLineItemChange(index, 'weighBridgeReceiptId', e.target.value)}
                          >
                            <option value="">Select Weigh Bridge Receipt (optional)</option>
                            {(() => {
                              const selectedIdsOnOtherLines = new Set(
                                grnLineItems
                                  .map((li, i) => (i !== index && li.weighBridgeReceiptId ? String(li.weighBridgeReceiptId) : null))
                                  .filter(Boolean)
                              );
                              const availableWBRs = unusedWeighBridgeReceipts.filter(wbr => {
                                if (selectedIdsOnOtherLines.has(String(wbr.id))) {
                                  return false;
                                }
                                if (lineItem.itemId && wbr.material?.id
                                  && String(wbr.material.id) !== String(lineItem.itemId)) {
                                  return false;
                                }
                                if (grnFormData.purchaseOrderId) {
                                  if (wbr.purchaseOrder?.id
                                    && String(wbr.purchaseOrder.id) !== String(grnFormData.purchaseOrderId)) {
                                    return false;
                                  }
                                }
                                return true;
                              });

                              return availableWBRs.map(wbr => {
                                const net = wbr.netWeight != null
                                  ? parseFloat(wbr.netWeight).toFixed(2)
                                  : '';
                                const materialName = wbr.material?.name || '';
                                const vehicle = wbr.vehicleNo ? ` / ${wbr.vehicleNo}` : '';
                                const ref = wbr.procurementSurvey?.surveyNumber
                                  ? ` / PS ${wbr.procurementSurvey.surveyNumber}`
                                  : (wbr.purchaseOrder?.poNumber ? ` / PO ${wbr.purchaseOrder.poNumber}` : '');
                                return (
                                  <option key={wbr.id} value={wbr.id}>
                                    {wbr.rstNumber}
                                    {net ? ` — ${net}` : ''}
                                    {materialName ? ` ${materialName}` : ''}
                                    {vehicle}
                                    {ref}
                                  </option>
                                );
                              });
                            })()}
                          </Form.Select>
                          <Form.Text className="text-muted">
                            Only unused weigh bridge receipts (not yet on a GRN). Selecting one fills received quantity from net weight.
                          </Form.Text>
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group>
                          <Form.Label>Vendor Lot Number</Form.Label>
                          <Form.Control
                            type="text"
                            value={lineItem.vendorLotNumber || ''}
                            onChange={(e) => handleGRNLineItemChange(index, 'vendorLotNumber', e.target.value)}
                            placeholder="Enter vendor lot number"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group>
                          <Form.Label>Internal Lot Number</Form.Label>
                          <Form.Control
                            type="text"
                            value={lineItem.internalLotNumber || ''}
                            onChange={(e) => handleGRNLineItemChange(index, 'internalLotNumber', e.target.value)}
                            placeholder="Auto-generated"
                            readOnly
                            className="bg-body-secondary"
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              ))}
            </div>

            {/* Service Items Section */}
            {grnServiceItems.length > 0 && (
              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <Form.Label className="mb-0 fw-bold">Service Items</Form.Label>
                </div>
                <Card className="bg-body-secondary">
                  <Card.Body>
                    <Table striped bordered size="sm">
                      <thead>
                        <tr>
                          <th>Service</th>
                          <th>Quantity</th>
                          <th>Unit Price</th>
                          <th>Total</th>
                          <th>Linked To Material Item</th>
                          <th>Vendor</th>
                        </tr>
                      </thead>
                      <tbody>
                        {grnServiceItems.map((serviceItem, serviceItemIndex) => {
                          // Find linked material item by itemId (from PO) or by selected index
                          let linkedMaterialIndex = null;
                          let linkedMaterialItem = null;
                          
                          // First check if linkedMaterialLineItemIndex is set (user selected in GRN)
                          if (serviceItem.linkedMaterialLineItemIndex !== '' && 
                              serviceItem.linkedMaterialLineItemIndex !== null && 
                              serviceItem.linkedMaterialLineItemIndex !== undefined) {
                            const index = parseInt(serviceItem.linkedMaterialLineItemIndex);
                            if (!isNaN(index) && index >= 0 && index < grnLineItems.length) {
                              linkedMaterialIndex = index;
                              linkedMaterialItem = grnLineItems[index];
                            }
                          } 
                          // Otherwise, check if linkedMaterialItemId is set (from PO)
                          else if (serviceItem.linkedMaterialItemId) {
                            // Find the material line item by matching itemId (handle both string and number)
                            const serviceItemId = String(serviceItem.linkedMaterialItemId);
                            const foundIndex = grnLineItems.findIndex(li => {
                              if (!li.itemId) return false;
                              return String(li.itemId) === serviceItemId;
                            });
                            
                            if (foundIndex >= 0 && foundIndex < grnLineItems.length) {
                              linkedMaterialIndex = foundIndex;
                              linkedMaterialItem = grnLineItems[foundIndex];
                            }
                          }
                          
                          // Determine if service is linked (either by index or by itemId)
                          const isLinked = linkedMaterialItem !== null || 
                            (serviceItem.linkedMaterialItemId !== null && serviceItem.linkedMaterialItemId !== undefined) ||
                            (serviceItem.linkedMaterialLineItemIndex !== '' && serviceItem.linkedMaterialLineItemIndex !== null && serviceItem.linkedMaterialLineItemIndex !== undefined);
                          
                          // For linked services, quantity should match the material item's received quantity
                          // Always use the current material quantity, not the stored service quantity
                          let displayQuantity;
                          if (isLinked && linkedMaterialItem) {
                            // Use the material item's received quantity
                            displayQuantity = parseFloat(linkedMaterialItem.receivedQuantity || 0);
                          } else if (isLinked && serviceItem.linkedMaterialItemId) {
                            // Linked but material not found by index - try to find it again
                            const serviceItemId = String(serviceItem.linkedMaterialItemId);
                            const materialItem = grnLineItems.find(li => li.itemId && String(li.itemId) === serviceItemId);
                            if (materialItem) {
                              displayQuantity = parseFloat(materialItem.receivedQuantity || 0);
                            } else {
                              // Material not found, but service is linked - use service quantity as fallback
                              displayQuantity = parseFloat(serviceItem.quantity || 0);
                            }
                          } else {
                            // Use the service item's own quantity for non-linked services
                            displayQuantity = parseFloat(serviceItem.quantity || 0);
                          }
                          
                          // Calculate line total based on display quantity
                          const displayLineTotal = displayQuantity * (parseFloat(serviceItem.unitPrice) || 0);
                          
                          return (
                            <tr key={serviceItemIndex}>
                              <td>{serviceItem.serviceName}</td>
                              <td>
                                {isLinked ? (
                                  <Form.Control
                                    type="text"
                                    size="sm"
                                    value={displayQuantity.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    readOnly
                                    className="bg-body-secondary"
                                    title="Quantity matches linked material item (read-only)"
                                  />
                                ) : (
                                  <Form.Control
                                    type="number"
                                    size="sm"
                                    step="0.01"
                                    min="0"
                                    value={displayQuantity}
                                    onChange={(e) => {
                                      const newQuantity = parseFloat(e.target.value) || 0;
                                      const updated = [...grnServiceItems];
                                      updated[serviceItemIndex].quantity = newQuantity;
                                      updated[serviceItemIndex].lineTotal = newQuantity * (parseFloat(updated[serviceItemIndex].unitPrice) || 0);
                                      setGrnServiceItems(updated);
                                    }}
                                  />
                                )}
                              </td>
                              <td>{formatCurrency(serviceItem.unitPrice)}</td>
                              <td>{formatCurrency(displayLineTotal)}</td>
                              <td>
                                <Form.Select
                                  size="sm"
                                  value={linkedMaterialIndex !== null && linkedMaterialIndex !== undefined ? linkedMaterialIndex.toString() : ''}
                                  onChange={(e) => {
                                    const updated = [...grnServiceItems];
                                    const selectedIndex = e.target.value;
                                    updated[serviceItemIndex].linkedMaterialLineItemIndex = selectedIndex;
                                    
                                    // If linking to a material item, update quantity to match
                                    if (selectedIndex !== '' && selectedIndex !== null) {
                                      const materialIndex = parseInt(selectedIndex);
                                      if (materialIndex >= 0 && materialIndex < grnLineItems.length) {
                                        const materialQty = parseFloat(grnLineItems[materialIndex].receivedQuantity || 0);
                                        updated[serviceItemIndex].quantity = materialQty;
                                        updated[serviceItemIndex].lineTotal = materialQty * (parseFloat(updated[serviceItemIndex].unitPrice) || 0);
                                      }
                                    } else {
                                      // Unlinking - keep current quantity
                                      updated[serviceItemIndex].linkedMaterialLineItemIndex = '';
                                    }
                                    setGrnServiceItems(updated);
                                  }}
                                >
                                  <option value="">None (Distributed to all items)</option>
                                  {grnLineItems
                                    .map((li, idx) => ({ li, idx }))
                                    .filter(({ li }) => li.itemId && li.itemId !== '')
                                    .map(({ li, idx }) => {
                                      const item = items.find(i => i.id === parseInt(li.itemId));
                                      return (
                                        <option key={idx} value={idx}>
                                          {item?.name || `Item ${li.itemId}`}
                                        </option>
                                      );
                                    })}
                                </Form.Select>
                              </td>
                              <td>{serviceItem.vendor?.name || '-'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowGRNModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              {selectedGRN ? 'Update' : 'Create'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Vendor Type Modal */}
      <Modal show={showVendorTypeModal} onHide={() => setShowVendorTypeModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{selectedVendorType ? 'Edit Vendor Type' : 'Create Vendor Type'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSaveVendorType}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Name *</Form.Label>
              <Form.Control
                type="text"
                value={vendorTypeFormData.name}
                onChange={(e) => setVendorTypeFormData({ ...vendorTypeFormData, name: e.target.value })}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={vendorTypeFormData.description}
                onChange={(e) => setVendorTypeFormData({ ...vendorTypeFormData, description: e.target.value })}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Status</Form.Label>
              <Form.Select
                value={vendorTypeFormData.isActive ? 'true' : 'false'}
                onChange={(e) => setVendorTypeFormData({ ...vendorTypeFormData, isActive: e.target.value === 'true' })}
              >
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </Form.Select>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowVendorTypeModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              {selectedVendorType ? 'Update' : 'Create'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Vendor Modal */}
      <Modal show={showVendorModal} onHide={() => setShowVendorModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{selectedVendor ? 'Edit Vendor' : 'Create Vendor'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSaveVendor}>
          <Modal.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Name *</Form.Label>
                  <Form.Control
                    type="text"
                    value={vendorFormData.name}
                    onChange={(e) => setVendorFormData({ ...vendorFormData, name: e.target.value })}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Company Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={vendorFormData.companyName}
                    onChange={(e) => setVendorFormData({ ...vendorFormData, companyName: e.target.value })}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Contact Person</Form.Label>
                  <Form.Control
                    type="text"
                    value={vendorFormData.contactPerson}
                    onChange={(e) => setVendorFormData({ ...vendorFormData, contactPerson: e.target.value })}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Vendor Type *</Form.Label>
                  <Form.Select
                    value={vendorFormData.vendorTypeId}
                    onChange={(e) => setVendorFormData({ ...vendorFormData, vendorTypeId: e.target.value })}
                    required
                  >
                    <option value="">Select Vendor Type</option>
                    {vendorTypes.filter(vt => vt.isActive).map(vt => (
                      <option key={vt.id} value={vt.id}>{vt.name}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    value={vendorFormData.email}
                    onChange={(e) => setVendorFormData({ ...vendorFormData, email: e.target.value })}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Phone Number *</Form.Label>
                  <PhoneNumberInput
                    name="phoneNumber"
                    value={vendorFormData.phoneNumber}
                    onChange={(e) => setVendorFormData({ ...vendorFormData, phoneNumber: e.target.value })}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Address</Form.Label>
              <Form.Control
                type="text"
                value={vendorFormData.address}
                onChange={(e) => setVendorFormData({ ...vendorFormData, address: e.target.value })}
              />
            </Form.Group>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>City *</Form.Label>
                  <Form.Control
                    type="text"
                    value={vendorFormData.city}
                    onChange={(e) => setVendorFormData({ ...vendorFormData, city: e.target.value })}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>District</Form.Label>
                  <Form.Control
                    type="text"
                    value={vendorFormData.district}
                    onChange={(e) => setVendorFormData({ ...vendorFormData, district: e.target.value })}
                    placeholder="District"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>State/Province *</Form.Label>
                  {selectedVendorCountryCode && selectedVendorCountryCode !== 'OTHER' && getStatesForCountry(selectedVendorCountryCode).length > 0 ? (
                    <Form.Select
                      value={vendorFormData.state}
                      onChange={(e) => setVendorFormData({ ...vendorFormData, state: e.target.value })}
                      required
                    >
                      <option value="">Select State/Province</option>
                      {getStatesForCountry(selectedVendorCountryCode).map(state => (
                        <option key={state} value={state}>{state}</option>
                      ))}
                    </Form.Select>
                  ) : (
                    <Form.Control
                      type="text"
                      value={vendorFormData.state}
                      onChange={(e) => setVendorFormData({ ...vendorFormData, state: e.target.value })}
                      placeholder="State/Province"
                      required
                    />
                  )}
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Country *</Form.Label>
                  <Form.Select
                    value={selectedVendorCountryCode}
                    onChange={(e) => {
                      const countryCode = e.target.value;
                      const countryObj = countries.find(c => c.code === countryCode);
                      const countryName = countryObj ? countryObj.name : '';
                      setSelectedVendorCountryCode(countryCode);
                      setVendorFormData({
                        ...vendorFormData,
                        country: countryName,
                        state: '' // Reset state when country changes
                      });
                    }}
                    required
                  >
                    <option value="">Select Country</option>
                    {countries.map(country => (
                      <option key={country.code} value={country.code}>{country.name}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Postal Code</Form.Label>
                  <Form.Control
                    type="text"
                    value={vendorFormData.postalCode}
                    onChange={(e) => setVendorFormData({ ...vendorFormData, postalCode: e.target.value })}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{indianVendor ? 'GSTIN' : 'Tax ID'}</Form.Label>
                  {indianVendor ? (
                    <GstinLookupInput
                      value={vendorFormData.taxId}
                      onChange={(e) => setVendorFormData({ ...vendorFormData, taxId: e.target.value.toUpperCase() })}
                      onResolved={handleVendorGstinResolved}
                      idleHint="Used for input tax credit on this vendor's invoices"
                    />
                  ) : (
                    <Form.Control
                      type="text"
                      value={vendorFormData.taxId}
                      onChange={(e) => setVendorFormData({ ...vendorFormData, taxId: e.target.value })}
                    />
                  )}
                  {indianVendor && !(vendorFormData.taxId || '').trim() && vendorFormData.gstRegistrationType !== 'UNREGISTERED' && (
                    <Form.Text className="text-warning d-block">
                      No GSTIN. You can save, but credit on this vendor&apos;s invoices can&apos;t be claimed until you add one.
                    </Form.Text>
                  )}
                </Form.Group>
                {indianVendor && (
                  <Form.Group className="mb-3" controlId="vendor-gst-registration">
                    <Form.Label>GST registration</Form.Label>
                    <Form.Select
                      value={vendorFormData.gstRegistrationType}
                      onChange={(e) => setVendorFormData({ ...vendorFormData, gstRegistrationType: e.target.value })}
                    >
                      <option value="">Not chosen (Regular with a GSTIN, otherwise Unregistered)</option>
                      <option value="REGULAR">Regular</option>
                      <option value="COMPOSITION">Composition (no input credit on their bills)</option>
                      <option value="UNREGISTERED">Not registered for GST</option>
                    </Form.Select>
                  </Form.Group>
                )}
                {indianVendor && tdsSettings?.enabled && (
                  <Row className="g-2">
                    <Col sm={7}>
                      <Form.Group className="mb-3" controlId="vendor-tds-section">
                        <Form.Label>TDS on payments</Form.Label>
                        <Form.Select
                          value={vendorFormData.tdsSection}
                          onChange={(e) => setVendorFormData({ ...vendorFormData, tdsSection: e.target.value })}
                        >
                          <option value="">No TDS</option>
                          {tdsSettings.sections.filter((x) => x.isActive || x.code === vendorFormData.tdsSection).map((x) => (
                            <option key={x.code} value={x.code}>{x.label}</option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col sm={5}>
                      <Form.Group className="mb-3" controlId="vendor-pan">
                        <Form.Label>PAN</Form.Label>
                        {GSTIN_PATTERN.test((vendorFormData.taxId || '').trim()) ? (
                          <div className="form-control-plaintext small">{vendorFormData.taxId.trim().slice(2, 12)} <span className="text-muted">(from the GSTIN)</span></div>
                        ) : (
                          <Form.Control
                            type="text"
                            maxLength={10}
                            value={vendorFormData.pan}
                            placeholder="ABCDE1234F"
                            onChange={(e) => setVendorFormData({ ...vendorFormData, pan: e.target.value.toUpperCase() })}
                          />
                        )}
                        {vendorFormData.tdsSection && !GSTIN_PATTERN.test((vendorFormData.taxId || '').trim()) && !vendorFormData.pan && (
                          <Form.Text className="text-warning d-block">Without a PAN, TDS is at the higher rate (usually 20%).</Form.Text>
                        )}
                      </Form.Group>
                    </Col>
                  </Row>
                )}
              </Col>
            </Row>
            <Row>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Credit days</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    max="365"
                    value={vendorFormData.creditDays}
                    onChange={(e) => setVendorFormData({ ...vendorFormData, creditDays: e.target.value })}
                    placeholder="e.g. 30"
                  />
                  <Form.Text className="text-muted">Their bills fall due this many days after the bill date</Form.Text>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Payment Terms</Form.Label>
                  <Form.Control
                    type="text"
                    value={vendorFormData.paymentTerms}
                    onChange={(e) => setVendorFormData({ ...vendorFormData, paymentTerms: e.target.value })}
                    placeholder="e.g., Net 30, COD"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Credit Limit</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    value={vendorFormData.creditLimit}
                    onChange={(e) => setVendorFormData({ ...vendorFormData, creditLimit: e.target.value })}
                  />
                </Form.Group>
              </Col>
            </Row>
            {indianVendor && (
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>MSME (Udyam) registration</Form.Label>
                    <Form.Select
                      value={vendorFormData.msmeCategory}
                      onChange={(e) => setVendorFormData({ ...vendorFormData, msmeCategory: e.target.value })}
                    >
                      <option value="">Not an MSME / don&apos;t know</option>
                      <option value="MICRO">Micro enterprise</option>
                      <option value="SMALL">Small enterprise</option>
                      <option value="MEDIUM">Medium enterprise</option>
                    </Form.Select>
                    <Form.Text className="text-muted">Micro and small enterprises must be paid within 45 days; Payables flags their bills.</Form.Text>
                  </Form.Group>
                </Col>
                {vendorFormData.msmeCategory && (
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Udyam number</Form.Label>
                      <Form.Control
                        type="text"
                        value={vendorFormData.udyamNumber}
                        onChange={(e) => setVendorFormData({ ...vendorFormData, udyamNumber: e.target.value })}
                        placeholder="UDYAM-MH-01-0001234"
                      />
                    </Form.Group>
                  </Col>
                )}
              </Row>
            )}
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Bank Account</Form.Label>
                  <Form.Control
                    type="text"
                    value={vendorFormData.bankAccount}
                    onChange={(e) => setVendorFormData({ ...vendorFormData, bankAccount: e.target.value })}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Bank Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={vendorFormData.bankName}
                    onChange={(e) => setVendorFormData({ ...vendorFormData, bankName: e.target.value })}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>IFSC Code</Form.Label>
                  <Form.Control
                    type="text"
                    value={vendorFormData.ifscCode}
                    onChange={(e) => setVendorFormData({ ...vendorFormData, ifscCode: e.target.value })}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Website</Form.Label>
                  <WebAddressInput
                    https
                    value={vendorFormData.website}
                    onChange={(v) => setVendorFormData({ ...vendorFormData, website: v })}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    value={vendorFormData.isActive ? 'true' : 'false'}
                    onChange={(e) => setVendorFormData({ ...vendorFormData, isActive: e.target.value === 'true' })}
                  >
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Notes</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={vendorFormData.notes}
                onChange={(e) => setVendorFormData({ ...vendorFormData, notes: e.target.value })}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowVendorModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              {selectedVendor ? 'Update' : 'Create'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Purchase Order Modal */}
      <Modal show={showPOModal} onHide={() => setShowPOModal(false)} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>{selectedPO ? 'Edit Purchase Order' : 'Create Purchase Order'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSavePO}>
          <Modal.Body>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>PO Number</Form.Label>
                  <Form.Control
                    type="text"
                    value={poFormData.poNumber}
                    onChange={(e) => setPoFormData({ ...poFormData, poNumber: e.target.value })}
                    placeholder="Auto-generated if left empty"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>PO Date *</Form.Label>
                  <DateInput
                   
                    value={poFormData.poDate}
                    onChange={(e) => setPoFormData({ ...poFormData, poDate: e.target.value })}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Required Date</Form.Label>
                  <DateInput
                   
                    value={poFormData.requiredDate}
                    onChange={(e) => setPoFormData({ ...poFormData, requiredDate: e.target.value })}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Warehouse *</Form.Label>
                  <Form.Select
                    value={poFormData.warehouseId}
                    onChange={(e) => setPoFormData({ ...poFormData, warehouseId: e.target.value })}
                    required
                  >
                    <option value="">Select Warehouse</option>
                    {warehouses.filter(w => w.isActive).map(wh => (
                      <option key={wh.id} value={wh.id}>{wh.name}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Reference Number</Form.Label>
                  <Form.Control
                    type="text"
                    value={poFormData.referenceNumber}
                    onChange={(e) => setPoFormData({ ...poFormData, referenceNumber: e.target.value })}
                    placeholder="Internal reference, requisition number, etc."
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <Form.Label className="mb-2">Address of Origin (Shipping Point)</Form.Label>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Street Address</Form.Label>
                  <Form.Control
                    type="text"
                    value={poFormData.addressOfOrigin}
                    onChange={(e) => setPoFormData({ ...poFormData, addressOfOrigin: e.target.value })}
                    placeholder="Street address from where items are being shipped"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>City</Form.Label>
                  <Form.Control
                    type="text"
                    value={poFormData.cityOfOrigin}
                    onChange={(e) => setPoFormData({ ...poFormData, cityOfOrigin: e.target.value })}
                    placeholder="City"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>District</Form.Label>
                  <Form.Control
                    type="text"
                    value={poFormData.districtOfOrigin}
                    onChange={(e) => setPoFormData({ ...poFormData, districtOfOrigin: e.target.value })}
                    placeholder="District"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>State/Province</Form.Label>
                  {selectedPOCountryCode && selectedPOCountryCode !== 'OTHER' && getStatesForCountry(selectedPOCountryCode).length > 0 ? (
                    <Form.Select
                      value={poFormData.stateOfOrigin}
                      onChange={(e) => setPoFormData({ ...poFormData, stateOfOrigin: e.target.value })}
                    >
                      <option value="">Select State/Province</option>
                      {getStatesForCountry(selectedPOCountryCode).map(state => (
                        <option key={state} value={state}>{state}</option>
                      ))}
                    </Form.Select>
                  ) : (
                    <Form.Control
                      type="text"
                      value={poFormData.stateOfOrigin}
                      onChange={(e) => setPoFormData({ ...poFormData, stateOfOrigin: e.target.value })}
                      placeholder="State/Province"
                    />
                  )}
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Country</Form.Label>
                  <Form.Select
                    value={selectedPOCountryCode}
                    onChange={(e) => {
                      const countryCode = e.target.value;
                      const countryObj = countries.find(c => c.code === countryCode);
                      const countryName = countryObj ? countryObj.name : '';
                      setSelectedPOCountryCode(countryCode);
                      setPoFormData({
                        ...poFormData,
                        countryOfOrigin: countryName,
                        stateOfOrigin: '' // Reset state when country changes
                      });
                    }}
                  >
                    <option value="">Select Country</option>
                    {countries.map(country => (
                      <option key={country.code} value={country.code}>{country.name}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <Form.Text className="text-muted mb-3 d-block">
                  The location from where items will be shipped to the organization warehouse
                </Form.Text>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Remarks</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={poFormData.remarks}
                    onChange={(e) => setPoFormData({ ...poFormData, remarks: e.target.value })}
                  />
                </Form.Group>
              </Col>
            </Row>
            {selectedPO && (
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Status</Form.Label>
                    <Form.Select
                      value={poFormData.status}
                      onChange={(e) => setPoFormData({ ...poFormData, status: e.target.value })}
                    >
                      <option value="DRAFT">DRAFT</option>
                      <option value="APPROVED">APPROVED</option>
                      <option value="PARTIAL">PARTIAL</option>
                      <option value="COMPLETED">COMPLETED</option>
                      <option value="CANCELLED">CANCELLED</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
            )}

            <h5 className="mt-4">Line Items (Multi-Vendor)</h5>
            <p className="text-muted small">
              Each line item can be assigned to a different vendor. 
              Amounts are displayed in {organizationCurrency} (default currency from organization settings).
            </p>
            <Table bordered size="sm">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Item/Service *</th>
                  <th>Vendor *</th>
                  <th>Quantity *</th>
                  <th>Unit Price ({organizationCurrency}) *</th>
                  <th>Line Total</th>
                  <th>UOM</th>
                  <th>Linked To Material</th>
                  <th>Remarks</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {poLineItems.map((lineItem, index) => {
                  const quantity = parseFloat(lineItem.quantity) || 0;
                  const unitPrice = parseFloat(lineItem.unitPrice) || 0;
                  const lineTotal = quantity * unitPrice;
                  
                  return (
                    <tr key={index}>
                      <td>
                        <Form.Select
                          value={lineItem.type}
                          onChange={(e) => handlePOLineItemChange(index, 'type', e.target.value)}
                          size="sm"
                        >
                          <option value="ITEM">Item</option>
                          <option value="SERVICE">Service</option>
                        </Form.Select>
                      </td>
                      <td>
                        {lineItem.type === 'ITEM' ? (
                          <Form.Select
                            value={lineItem.itemId}
                            onChange={(e) => handlePOLineItemChange(index, 'itemId', e.target.value)}
                            required
                            size="sm"
                          >
                            <option value="">Select Item</option>
                            {organizationMaterials
                              .filter(orgMaterial => orgMaterial.item && orgMaterial.item.isActive)
                              .map(orgMaterial => (
                                <option key={orgMaterial.item.id} value={orgMaterial.item.id}>
                                  {orgMaterial.item.name} {orgMaterial.sku ? `(${orgMaterial.sku})` : ''}
                                </option>
                              ))}
                          </Form.Select>
                        ) : (
                          <Form.Select
                            value={lineItem.serviceId}
                            onChange={(e) => handlePOLineItemChange(index, 'serviceId', e.target.value)}
                            required
                            size="sm"
                          >
                            <option value="">Select Service</option>
                            {services.map(service => (
                              <option key={service.id} value={service.id}>{service.name}</option>
                            ))}
                          </Form.Select>
                        )}
                      </td>
                      <td>
                        <Form.Select
                          value={lineItem.vendorId}
                          onChange={(e) => handlePOLineItemChange(index, 'vendorId', e.target.value)}
                          required
                          size="sm"
                        >
                          <option value="">Select Vendor</option>
                          {vendors.filter(v => v.isActive).map(vendor => (
                            <option key={vendor.id} value={vendor.id}>{vendor.name}</option>
                          ))}
                        </Form.Select>
                      </td>
                      <td>
                        <Form.Control
                          type="number"
                          step="0.001"
                          value={lineItem.quantity}
                          onChange={(e) => handlePOLineItemChange(index, 'quantity', e.target.value)}
                          required
                          size="sm"
                        />
                      </td>
                      <td>
                        <Form.Control
                          type="number"
                          step="0.01"
                          value={lineItem.unitPrice}
                          onChange={(e) => handlePOLineItemChange(index, 'unitPrice', e.target.value)}
                          required
                          size="sm"
                        />
                        <LastRateHint last={lastPoRate(lineItem)} current={lineItem.unitPrice}
                          onUse={() => handlePOLineItemChange(index, 'unitPrice', String(Number(lastPoRate(lineItem).rate)))} />
                      </td>
                      <td>
                        <strong>{formatCurrency(lineTotal)}</strong>
                      </td>
                      <td>
                        <Form.Select
                          value={lineItem.unitOfMeasurementId}
                          onChange={(e) => handlePOLineItemChange(index, 'unitOfMeasurementId', e.target.value)}
                          size="sm"
                        >
                          <option value="">Select UOM</option>
                          {uoms.map(uom => (
                            <option key={uom.id} value={uom.id}>{uom.symbol} ({uom.name})</option>
                          ))}
                        </Form.Select>
                      </td>
                      <td>
                        {lineItem.type === 'SERVICE' ? (
                          <Form.Select
                            value={lineItem.linkedMaterialLineItemIndex}
                            onChange={(e) => handlePOLineItemChange(index, 'linkedMaterialLineItemIndex', e.target.value)}
                            size="sm"
                          >
                            <option value="">None</option>
                            {poLineItems
                              .map((li, idx) => ({ li, idx }))
                              .filter(({ li, idx }) => li.type === 'ITEM' && idx !== index && li.itemId)
                              .map(({ li, idx }) => {
                                const item = items.find(i => i.id === parseInt(li.itemId));
                                return (
                                  <option key={idx} value={idx}>
                                    Line {idx + 1}: {item ? item.name : 'Item'}
                                  </option>
                                );
                              })}
                          </Form.Select>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td>
                        <Form.Control
                          type="text"
                          value={lineItem.remarks}
                          onChange={(e) => handlePOLineItemChange(index, 'remarks', e.target.value)}
                          size="sm"
                          placeholder="Line item remarks"
                        />
                      </td>
                      <td>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleRemovePOLineItem(index)}
                          title="Remove"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                            <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
                            <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
                          </svg>
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
            <Button variant="secondary" size="sm" onClick={handleAddPOLineItem} className="mt-2">
              + Add Line Item
            </Button>
            
            {/* Calculate and show total */}
            {poLineItems.length > 0 && (
              <div className="mt-3">
                <Card className="bg-body-secondary border">
                  <Card.Body className="py-2">
                    <div className="d-flex justify-content-between align-items-center">
                      <strong className="text-body">Total Amount:</strong>
                      <strong className="text-body-emphasis">
                        {formatCurrency(poLineItems.reduce((sum, li) => {
                          const qty = parseFloat(li.quantity) || 0;
                          const price = parseFloat(li.unitPrice) || 0;
                          return sum + (qty * price);
                        }, 0))}
                      </strong>
                    </div>
                  </Card.Body>
                </Card>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowPOModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              {selectedPO ? 'Update Purchase Order' : 'Create Purchase Order'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Purchase Order Transactions Modal (Deliveries & Payments) */}
      <Modal
        show={showPOTransactionsModal}
        onHide={() => { setShowPOTransactionsModal(false); setSelectedPOForTransactions(null); }}
        size="lg"
        className="print-modal"
      >
        <Modal.Header className="print-header">
          <Modal.Title>Transactions — {selectedPOForTransactions?.poNumber || 'Purchase Order'}</Modal.Title>
          <div className="print-actions">
            <Button
              variant="primary"
              className="me-2"
              onClick={handlePrint}
              disabled={loadingPOTransactions}
            >
              Print
            </Button>
            <Button
              variant="secondary"
              onClick={() => { setShowPOTransactionsModal(false); setSelectedPOForTransactions(null); }}
            >
              Close
            </Button>
          </div>
        </Modal.Header>
        <Modal.Body className="print-body">
          {loadingPOTransactions ? (
            <div className="d-flex justify-content-center py-4">
              <Spinner animation="border" role="status"><span className="visually-hidden">Loading...</span></Spinner>
            </div>
          ) : (
            <OrgPrintChrome>
              <div className="print-content">
                <div className="text-center mb-3">
                  <h2>PURCHASE ORDER TRANSACTIONS</h2>
                  <p className="text-muted mb-0">
                    PO Number: <strong>{selectedPOForTransactions?.poNumber || '-'}</strong>
                  </p>
                </div>

                <h6 className="mb-2">Deliveries (GRNs)</h6>
                {poTransactions.deliveries && poTransactions.deliveries.length > 0 ? (
                  <div className="mb-4">
                    {poTransactions.deliveries.map((d, idx) => (
                      <div key={d.id != null ? d.id : `d-${idx}`} className="mb-3">
                        <p className="mb-1">
                          <strong>{d.grnNumber || '-'}</strong>
                          {' — '}{formatDate(d.grnDate) || '-'}
                          {' — '}{d.warehouseName || d.retailStoreName || d.productionUnitName || '-'}
                          {d.status && (
                            <>
                              {' — '}
                              <Badge bg={d.status === 'RECEIVED' || d.status === 'COMPLETED' ? 'success' : 'secondary'}>
                                {d.status}
                              </Badge>
                            </>
                          )}
                        </p>
                        {d.lineItems && d.lineItems.length > 0 ? (
                          <Table size="sm" striped bordered className="mb-0">
                            <thead>
                              <tr>
                                <th>#</th>
                                <th>Type</th>
                                <th>Item / Service</th>
                                <th>Vendor</th>
                                <th>Received Qty</th>
                                <th>UOM</th>
                                <th>Unit Price</th>
                                <th>Line Total</th>
                              </tr>
                            </thead>
                            <tbody>
                              {d.lineItems.map((li, liIdx) => {
                                const name = li.itemName || li.serviceName || '-';
                                const qty = li.receivedQuantity != null
                                  ? parseFloat(li.receivedQuantity).toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 })
                                  : '-';
                                return (
                                  <tr key={li.id != null ? li.id : `li-${liIdx}`}>
                                    <td>{liIdx + 1}</td>
                                    <td>{li.lineType || (li.serviceName ? 'SERVICE' : 'MATERIAL')}</td>
                                    <td>{name}</td>
                                    <td>{li.vendorName || '-'}</td>
                                    <td>{qty}</td>
                                    <td>{li.uomSymbol || '-'}</td>
                                    <td>{li.unitPrice != null ? formatCurrency(Number(li.unitPrice)) : '-'}</td>
                                    <td>{li.lineTotal != null ? formatCurrency(Number(li.lineTotal)) : '-'}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </Table>
                        ) : (
                          <p className="text-muted small mb-0">No line items on this GRN.</p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted mb-4">No deliveries (GRNs) against this PO yet.</p>
                )}
                <h6 className="mb-2">Payments received by vendor</h6>
                {poTransactions.payments && poTransactions.payments.length > 0 ? (
                  <Table size="sm" striped bordered>
                    <thead>
                      <tr>
                        <th>Payment #</th>
                        <th>Date</th>
                        <th>Applied amount</th>
                        <th>Method</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {poTransactions.payments.map((p, idx) => (
                        <tr key={p.id != null ? `p-${p.id}-${idx}` : `p-${idx}`}>
                          <td>{p.paymentNumber || '-'}</td>
                          <td>{formatDate(p.paymentDate) || '-'}</td>
                          <td>{p.appliedAmount != null ? formatCurrency(Number(p.appliedAmount)) : '-'}</td>
                          <td>{p.paymentMethod || '-'}</td>
                          <td>{p.status ? <Badge bg={p.status === 'RECORDED' ? 'success' : 'secondary'}>{p.status}</Badge> : '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                ) : (
                  <p className="text-muted">No payments applied to this PO yet.</p>
                )}
              </div>
            </OrgPrintChrome>
          )}
        </Modal.Body>
      </Modal>

      {/* Weigh Bridge Receipt Modal */}
      <Modal show={showWBRModal} onHide={() => setShowWBRModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{selectedWBR ? 'Edit Weigh Bridge Receipt' : 'Create Weigh Bridge Receipt'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSaveWBR}>
          <Modal.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>RST Number</Form.Label>
                  <Form.Control
                    type="text"
                    value={wbrFormData.rstNumber}
                    onChange={(e) => setWbrFormData({ ...wbrFormData, rstNumber: e.target.value })}
                    placeholder="Auto-generated if left empty"
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Date *</Form.Label>
                  <DateInput
                   
                    value={wbrFormData.receiptDate}
                    onChange={(e) => setWbrFormData({ ...wbrFormData, receiptDate: e.target.value })}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Time *</Form.Label>
                  <Form.Control
                    type="time"
                    value={wbrFormData.receiptTime}
                    onChange={(e) => setWbrFormData({ ...wbrFormData, receiptTime: e.target.value })}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Vendor</Form.Label>
                  <Form.Select
                    value={wbrFormData.vendorId}
                    onChange={(e) => {
                      const vendorId = e.target.value;
                      setWbrFormData(prev => {
                        const next = { ...prev, vendorId };
                        if (prev.purchaseOrderId) {
                          const selectedPO = purchaseOrders.find(po => po.id === parseInt(prev.purchaseOrderId));
                          if (selectedPO && !isPOMatchingFilter(selectedPO, vendorId, prev.materialItemId)) {
                            next.purchaseOrderId = '';
                          }
                        }
                        if (prev.procurementSurveyId) {
                          const selectedSurvey = openProcurementSurveys.find(s => String(s.id) === String(prev.procurementSurveyId))
                            || (selectedWBR?.procurementSurvey?.id === parseInt(prev.procurementSurveyId) ? selectedWBR.procurementSurvey : null);
                          if (selectedSurvey && !isSurveyMatchingFilter(selectedSurvey, vendorId, prev.materialItemId)) {
                            next.procurementSurveyId = '';
                          }
                        }
                        return next;
                      });
                    }}
                  >
                    <option value="">Select Vendor</option>
                    {vendors.filter(v => v.isActive).map(vendor => (
                      <option key={vendor.id} value={vendor.id}>{vendor.name}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Transporter</Form.Label>
                  <Form.Select
                    value={wbrFormData.transporterVendorId}
                    onChange={(e) => setWbrFormData({ ...wbrFormData, transporterVendorId: e.target.value })}
                  >
                    <option value="">Select Transporter</option>
                    {vendors
                      .filter(v => v.isActive && v.vendorType && 
                        (v.vendorType.name.toUpperCase().includes('H&T') || 
                         v.vendorType.name.toUpperCase().includes('TRANSPORTER')))
                      .map(vendor => (
                        <option key={vendor.id} value={vendor.id}>
                          {vendor.name} {vendor.vendorType ? `(${vendor.vendorType.name})` : ''}
                        </option>
                      ))}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Only vendors of type "H&T" or "Transporter" are shown
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Vehicle Number</Form.Label>
                  <Form.Control
                    type="text"
                    value={wbrFormData.vehicleNo}
                    onChange={(e) => setWbrFormData({ ...wbrFormData, vehicleNo: e.target.value })}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Material</Form.Label>
                  <Form.Select
                    value={wbrFormData.materialItemId}
                    onChange={(e) => {
                      const materialItemId = e.target.value;
                      setWbrFormData(prev => {
                        const next = { ...prev, materialItemId };
                        if (prev.purchaseOrderId) {
                          const selectedPO = purchaseOrders.find(po => po.id === parseInt(prev.purchaseOrderId));
                          if (selectedPO && !isPOMatchingFilter(selectedPO, prev.vendorId, materialItemId)) {
                            next.purchaseOrderId = '';
                          }
                        }
                        if (prev.procurementSurveyId) {
                          const selectedSurvey = openProcurementSurveys.find(s => String(s.id) === String(prev.procurementSurveyId))
                            || (selectedWBR?.procurementSurvey?.id === parseInt(prev.procurementSurveyId) ? selectedWBR.procurementSurvey : null);
                          if (selectedSurvey && !isSurveyMatchingFilter(selectedSurvey, prev.vendorId, materialItemId)) {
                            next.procurementSurveyId = '';
                          }
                        }
                        return next;
                      });
                    }}
                  >
                    <option value="">Select Material</option>
                    {organizationMaterials
                      .filter(orgMaterial => orgMaterial.item && orgMaterial.item.isActive)
                      .map(orgMaterial => (
                        <option key={orgMaterial.item.id} value={orgMaterial.item.id}>
                          {orgMaterial.item.name} {orgMaterial.sku ? `(${orgMaterial.sku})` : ''}
                        </option>
                      ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Reference Type</Form.Label>
                  <Form.Select
                    value={wbrFormData.referenceType}
                    onChange={(e) => {
                      const referenceType = e.target.value;
                      setWbrFormData(prev => ({
                        ...prev,
                        referenceType,
                        purchaseOrderId: referenceType === 'PURCHASE_ORDER' ? prev.purchaseOrderId : '',
                        procurementSurveyId: referenceType === 'PROCUREMENT_SURVEY' ? prev.procurementSurveyId : ''
                      }));
                    }}
                  >
                    <option value="">None</option>
                    <option value="PURCHASE_ORDER">Purchase Order</option>
                    <option value="PROCUREMENT_SURVEY">Procurement Survey / Field Slip</option>
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Link this receipt to a PO or an open field survey (optional)
                  </Form.Text>
                </Form.Group>
              </Col>
              {wbrFormData.referenceType === 'PURCHASE_ORDER' && (
                <Col md={8}>
                  <Form.Group className="mb-3">
                    <Form.Label>Purchase Order *</Form.Label>
                    <Form.Select
                      value={wbrFormData.purchaseOrderId}
                      onChange={(e) => {
                        const poId = e.target.value;
                        setWbrFormData(prev => ({ ...prev, purchaseOrderId: poId, procurementSurveyId: '' }));

                        if (poId) {
                          const selectedPO = purchaseOrders.find(po => po.id === parseInt(poId));
                          if (selectedPO) {
                            const isAddressEmpty = !wbrFormData.address && !wbrFormData.city &&
                              !wbrFormData.district && !wbrFormData.state && !wbrFormData.country;

                            if (isAddressEmpty && selectedPO.addressOfOrigin) {
                              const countryName = selectedPO.countryOfOrigin || '';
                              const countryObj = countries.find(c => c.name === countryName);
                              setSelectedWBRCountryCode(countryObj ? countryObj.code : '');
                              setWbrFormData(prev => ({
                                ...prev,
                                purchaseOrderId: poId,
                                procurementSurveyId: '',
                                address: selectedPO.addressOfOrigin || prev.address,
                                city: selectedPO.cityOfOrigin || prev.city,
                                district: selectedPO.districtOfOrigin || prev.district,
                                state: selectedPO.stateOfOrigin || prev.state,
                                country: countryName || prev.country
                              }));
                            }
                          }
                        }
                      }}
                      required
                    >
                      <option value="">Select Purchase Order</option>
                      {purchaseOrders
                        .filter(po => {
                          if (po.status === 'COMPLETED' || po.status === 'CANCELLED') {
                            return false;
                          }
                          return isPOMatchingFilter(po, wbrFormData.vendorId, wbrFormData.materialItemId);
                        })
                        .map(po => (
                          <option key={po.id} value={po.id}>
                            {po.poNumber} {po.poDate ? `(${formatDate(po.poDate)})` : ''}
                          </option>
                        ))}
                    </Form.Select>
                    <Form.Text className="text-muted">
                      {wbrFormData.vendorId && wbrFormData.materialItemId
                        ? 'Only POs with the selected vendor and material are shown. Address of Origin auto-fills if empty.'
                        : 'Only incomplete Purchase Orders are shown. Address of Origin auto-fills if empty.'}
                    </Form.Text>
                  </Form.Group>
                </Col>
              )}
              {wbrFormData.referenceType === 'PROCUREMENT_SURVEY' && (
                <Col md={8}>
                  <Form.Group className="mb-3">
                    <Form.Label>Procurement Survey *</Form.Label>
                    <Form.Select
                      value={wbrFormData.procurementSurveyId}
                      onChange={(e) => {
                        const surveyId = e.target.value;
                        const survey = openProcurementSurveys.find(s => String(s.id) === String(surveyId))
                          || (selectedWBR?.procurementSurvey && String(selectedWBR.procurementSurvey.id) === String(surveyId)
                            ? selectedWBR.procurementSurvey
                            : null);

                        if (!surveyId || !survey) {
                          setWbrFormData(prev => ({ ...prev, procurementSurveyId: surveyId, purchaseOrderId: '' }));
                          return;
                        }

                        const countryName = survey.country || '';
                        const countryObj = countries.find(c => c.name === countryName);
                        if (countryObj) {
                          setSelectedWBRCountryCode(countryObj.code);
                        }

                        setWbrFormData(prev => ({
                          ...prev,
                          procurementSurveyId: surveyId,
                          purchaseOrderId: '',
                          vendorId: survey.vendor?.id ? String(survey.vendor.id) : prev.vendorId,
                          materialItemId: survey.item?.id ? String(survey.item.id) : prev.materialItemId,
                          transporterVendorId: survey.htVendor?.id ? String(survey.htVendor.id) : prev.transporterVendorId,
                          address: survey.address || prev.address,
                          city: survey.city || prev.city,
                          district: survey.district || prev.district,
                          state: survey.state || prev.state,
                          country: countryName || prev.country,
                          pinCode: survey.postalCode || prev.pinCode
                        }));
                      }}
                      required
                    >
                      <option value="">Select Procurement Survey</option>
                      {(() => {
                        const options = [...openProcurementSurveys];
                        if (selectedWBR?.procurementSurvey?.id
                          && !options.some(s => s.id === selectedWBR.procurementSurvey.id)) {
                          options.unshift(selectedWBR.procurementSurvey);
                        }
                        return options
                          .filter(s => isSurveyMatchingFilter(s, wbrFormData.vendorId, wbrFormData.materialItemId))
                          .map(s => (
                            <option key={s.id} value={s.id}>
                              {s.surveyNumber} {s.surveyDate ? `(${formatDate(s.surveyDate)})` : ''}
                              {s.item?.name ? ` — ${s.item.name}` : ''}
                              {s.vendor?.name ? ` — ${s.vendor.name}` : ''}
                              {s.siteName || s.city ? ` / ${[s.siteName, s.city].filter(Boolean).join(', ')}` : ''}
                            </option>
                          ));
                      })()}
                    </Form.Select>
                    <Form.Text className="text-muted">
                      New or In Progress surveys from Purchase → Procurement Surveys. Vendor, material, transporter, and address autofill from the survey.
                    </Form.Text>
                  </Form.Group>
                </Col>
              )}
            </Row>
            <div className="mb-3">
              <Form.Label className="fw-bold">Address of Origin</Form.Label>
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Address</Form.Label>
                    <Form.Control
                      type="text"
                      value={wbrFormData.address}
                      onChange={(e) => setWbrFormData({ ...wbrFormData, address: e.target.value })}
                      placeholder="Street address"
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>City</Form.Label>
                    <Form.Control
                      type="text"
                      value={wbrFormData.city}
                      onChange={(e) => setWbrFormData({ ...wbrFormData, city: e.target.value })}
                      placeholder="City"
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>District</Form.Label>
                    <Form.Control
                      type="text"
                      value={wbrFormData.district}
                      onChange={(e) => setWbrFormData({ ...wbrFormData, district: e.target.value })}
                      placeholder="District"
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>State/Province</Form.Label>
                    {selectedWBRCountryCode && selectedWBRCountryCode !== 'OTHER' && getStatesForCountry(selectedWBRCountryCode).length > 0 ? (
                      <Form.Select
                        value={wbrFormData.state}
                        onChange={(e) => setWbrFormData({ ...wbrFormData, state: e.target.value })}
                      >
                        <option value="">Select State/Province</option>
                        {getStatesForCountry(selectedWBRCountryCode).map(state => (
                          <option key={state} value={state}>{state}</option>
                        ))}
                      </Form.Select>
                    ) : (
                      <Form.Control
                        type="text"
                        value={wbrFormData.state}
                        onChange={(e) => setWbrFormData({ ...wbrFormData, state: e.target.value })}
                        placeholder="State/Province"
                      />
                    )}
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Country</Form.Label>
                    <Form.Select
                      value={selectedWBRCountryCode}
                      onChange={(e) => {
                        const countryCode = e.target.value;
                        const countryObj = countries.find(c => c.code === countryCode);
                        const countryName = countryObj ? countryObj.name : '';
                        setSelectedWBRCountryCode(countryCode);
                        setWbrFormData({
                          ...wbrFormData,
                          country: countryName,
                          state: '' // Reset state when country changes
                        });
                      }}
                    >
                      <option value="">Select Country</option>
                      {countries.map(country => (
                        <option key={country.code} value={country.code}>{country.name}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Pin Code / Zip Code</Form.Label>
                    <Form.Control
                      type="text"
                      value={wbrFormData.pinCode}
                      onChange={(e) => setWbrFormData({ ...wbrFormData, pinCode: e.target.value })}
                      placeholder="Pin/Zip Code"
                    />
                  </Form.Group>
                </Col>
              </Row>
            </div>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Gross Weight (kg) *</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.001"
                    value={wbrFormData.grossWeight}
                    onChange={(e) => {
                      const value = e.target.value;
                      setWbrFormData({ ...wbrFormData, grossWeight: value });
                      // Auto-calculate net weight if both weights are present
                      if (value && wbrFormData.emptyWeight) {
                        const gross = parseFloat(value);
                        const empty = parseFloat(wbrFormData.emptyWeight);
                        if (gross > empty) {
                          // Net weight will be calculated on backend
                        }
                      }
                    }}
                    required
                    placeholder="Loaded vehicle weight"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Empty Weight (kg) *</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.001"
                    value={wbrFormData.emptyWeight}
                    onChange={(e) => {
                      const value = e.target.value;
                      setWbrFormData({ ...wbrFormData, emptyWeight: value });
                      // Auto-calculate net weight if both weights are present
                      if (value && wbrFormData.grossWeight) {
                        const gross = parseFloat(wbrFormData.grossWeight);
                        const empty = parseFloat(value);
                        if (gross > empty) {
                          // Net weight will be calculated on backend
                        }
                      }
                    }}
                    required
                    placeholder="Empty vehicle weight"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Net Weight (kg)</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.001"
                    value={
                      wbrFormData.grossWeight && wbrFormData.emptyWeight
                        ? (parseFloat(wbrFormData.grossWeight) - parseFloat(wbrFormData.emptyWeight)).toFixed(3)
                        : ''
                    }
                    readOnly
                    className="bg-body-secondary"
                  />
                  <Form.Text className="text-muted">
                    Calculated automatically (Gross - Empty)
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Weigh Bridge Operator *</Form.Label>
                  <Form.Select
                    value={wbrFormData.weighBridgeOperatorId}
                    onChange={(e) => setWbrFormData({ ...wbrFormData, weighBridgeOperatorId: e.target.value })}
                    required
                  >
                    <option value="">Select Operator</option>
                    {users.map(u => (
                      <option key={u.id} value={u.id}>
                        {u.firstName} {u.lastName} ({u.email})
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Remarks</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={wbrFormData.remarks}
                onChange={(e) => setWbrFormData({ ...wbrFormData, remarks: e.target.value })}
                placeholder="Additional notes or remarks"
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowWBRModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              {selectedWBR ? 'Update Receipt' : 'Create Receipt'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Purchase order document: one vendor's copy, printed or sent to the vendor */}
      <Modal show={Boolean(printingPO)} onHide={() => setPrintingPO(null)} size="xl" className="print-modal">
        <Modal.Header className="print-header">
          <Modal.Title>Purchase Order {printingPO?.poNumber}</Modal.Title>
          <div className="print-actions">
            <Button variant="primary" onClick={handlePrint} className="me-2">Print</Button>
            <Button variant="secondary" onClick={() => setPrintingPO(null)}>Close</Button>
          </div>
        </Modal.Header>
        <Modal.Body className="print-body">
          {printingPO && (
            <PurchaseOrderPrint po={printingPO} currency={organizationCurrency}
              onSend={(po, vendor) => { setPrintingPO(null); setSendingPO({ po, vendor }); }} />
          )}
        </Modal.Body>
      </Modal>
      {sendingPO && (
        <WhatsAppShareModal path={`purchase-orders/${sendingPO.po.id}/vendors/${sendingPO.vendor.id}`}
          stopPath={(link) => `/api/share/purchase-orders/links/${link.id}/revoke`}
          title={`Send ${sendingPO.po.poNumber} to ${sendingPO.vendor.name}`} what="purchase order" party="vendor"
          offerUpi={false} onHide={() => setSendingPO(null)} />
      )}

      {/* Print View Modal */}
      <Modal 
        show={showPrintModal} 
        onHide={() => setShowPrintModal(false)} 
        size="xl"
        className="print-modal"
      >
        <Modal.Header className="print-header">
          <Modal.Title>
            {printType === 'GRN' && 'Goods Receipt Note'}
            {printType === 'WBR' && 'Weigh Bridge Receipt'}
            {printType === 'VENDOR_TYPE' && 'Vendor Type'}
            {printType === 'VENDOR' && 'Vendor'}
            {printType === 'VENDOR_PAYMENT' && 'Vendor Payment'}
          </Modal.Title>
          <div className="print-actions">
            <Button variant="primary" onClick={handlePrint} className="me-2">
              Print
            </Button>
            <Button variant="secondary" onClick={() => setShowPrintModal(false)}>
              Close
            </Button>
          </div>
        </Modal.Header>
        <Modal.Body className="print-body">
          <OrgPrintChrome>
          {printType === 'GRN' && printData && (
            <div className="print-content">
              <div className="text-center mb-4">
                <h2>GOODS RECEIPT NOTE</h2>
                <p className="text-muted">GRN Number: <strong>{printData.grnNumber}</strong></p>
              </div>
              <Row className="mb-3">
                <Col md={6}>
                  <p><strong>GRN Date:</strong> {formatDate(printData.grnDate)}</p>
                  <p><strong>Warehouse:</strong> {printData.warehouse?.name || '-'}</p>
                  {printData.purchaseOrder && (
                    <p><strong>Purchase Order:</strong> {printData.purchaseOrder.poNumber || '-'}</p>
                  )}
                </Col>
                <Col md={6}>
                  <p><strong>Status:</strong> {printData.status}</p>
                </Col>
              </Row>
              {printData.lineItems && printData.lineItems.length > 0 && (
                <>
                  <h5 className="mt-3 mb-2">Goods Received</h5>
                  <Table striped bordered className="mt-3">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Item</th>
                        <th>Vendor</th>
                        <th>Quantity</th>
                        <th>Unit Price</th>
                        <th>Line Total</th>
                        <th>Weigh Bridge Receipt</th>
                      </tr>
                    </thead>
                    <tbody>
                      {printData.lineItems.map((item, idx) => {
                        const uom = item.unitOfMeasurement?.symbol || item.unitOfMeasurement?.name || '';
                        const quantity = parseFloat(item.receivedQuantity || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                        return (
                          <tr key={idx}>
                            <td>{idx + 1}</td>
                            <td>{item.item?.name || '-'}</td>
                            <td>{item.vendor?.name || '-'}</td>
                            <td>{quantity}{uom ? ` ${uom}` : ''}</td>
                            <td>{formatCurrency(item.unitPrice)}</td>
                            <td>{formatCurrency(item.lineTotal)}</td>
                            <td>{item.weighBridgeReceiptNo || '-'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </>
              )}
              
              {printData.serviceItems && printData.serviceItems.length > 0 && (
                <>
                  <h5 className="mt-4 mb-2">Services Received</h5>
                  <Table striped bordered className="mt-3">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Service</th>
                        <th>Vendor</th>
                        <th>Quantity</th>
                        <th>Unit Price</th>
                        <th>Line Total</th>
                        <th>Remarks</th>
                      </tr>
                    </thead>
                    <tbody>
                      {printData.serviceItems.map((serviceItem, idx) => {
                        const uom = serviceItem.unitOfMeasurement?.symbol || serviceItem.unitOfMeasurement?.name || '';
                        const quantity = parseFloat(serviceItem.quantity || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                        return (
                          <tr key={idx}>
                            <td>{idx + 1}</td>
                            <td>{serviceItem.service?.name || '-'}</td>
                            <td>{serviceItem.vendor?.name || '-'}</td>
                            <td>{quantity}{uom ? ` ${uom}` : ''}</td>
                            <td>{formatCurrency(serviceItem.unitPrice)}</td>
                            <td>{formatCurrency(serviceItem.lineTotal)}</td>
                            <td>{serviceItem.remarks || '-'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </>
              )}
              
              {(printData.lineItems?.length > 0 || printData.serviceItems?.length > 0) && (
                <div className="mt-3">
                  <Table bordered>
                    <tfoot>
                      <tr>
                        <td colSpan="5" className="text-end"><strong>Total Amount:</strong></td>
                        <td colSpan="2">
                          <strong>
                            {formatCurrency(
                              (printData.lineItems || []).reduce((sum, item) => 
                                sum + (parseFloat(item.lineTotal || 0)), 0
                              ) +
                              (printData.serviceItems || []).reduce((sum, serviceItem) => 
                                sum + (parseFloat(serviceItem.lineTotal || 0)), 0
                              )
                            )}
                          </strong>
                        </td>
                      </tr>
                    </tfoot>
                  </Table>
                </div>
              )}
              {printData.remarks && (
                <div className="mt-3">
                  <p><strong>Remarks:</strong> {printData.remarks}</p>
                </div>
              )}
            </div>
          )}

          {printType === 'WBR' && printData && (() => {
            // Get UOM from PO line item or survey, otherwise default to 'kg'
            let uom = 'kg';
            if (printData.purchaseOrder?.lineItems) {
              const materialLineItem = printData.purchaseOrder.lineItems.find(
                li => li.item?.id === printData.material?.id
              );
              if (materialLineItem?.unitOfMeasurement) {
                uom = materialLineItem.unitOfMeasurement.symbol || materialLineItem.unitOfMeasurement.name || 'kg';
              }
            } else if (printData.procurementSurvey?.unitOfMeasurement) {
              uom = printData.procurementSurvey.unitOfMeasurement.symbol
                || printData.procurementSurvey.unitOfMeasurement.name
                || 'kg';
            }
            
            return (
              <div className="print-content">
                <div className="text-center mb-4">
                  <h2>WEIGH BRIDGE RECEIPT</h2>
                  <p className="text-muted">RST Number: <strong>{printData.rstNumber}</strong></p>
                </div>
                <Row className="mb-3">
                  <Col md={6}>
                    <p><strong>Receipt Date:</strong> {formatDate(printData.receiptDate)}</p>
                    <p><strong>Receipt Time:</strong> {printData.receiptTime}</p>
                    <p><strong>Material:</strong> {printData.material?.name || '-'}</p>
                    {printData.purchaseOrder && (
                      <p><strong>Purchase Order:</strong> {printData.purchaseOrder.poNumber || '-'}</p>
                    )}
                    {printData.procurementSurvey && (
                      <p><strong>Procurement Survey:</strong> {printData.procurementSurvey.surveyNumber || '-'}</p>
                    )}
                  </Col>
                  <Col md={6}>
                    <p><strong>Vehicle No:</strong> {printData.vehicleNo || '-'}</p>
                    <p><strong>Vendor:</strong> {printData.vendor?.name || '-'}</p>
                    {printData.transporter && (
                      <p><strong>Transporter:</strong> {printData.transporter.name || '-'}</p>
                    )}
                    <p><strong>Operator:</strong> {printData.weighBridgeOperator ? `${printData.weighBridgeOperator.firstName} ${printData.weighBridgeOperator.lastName}` : '-'}</p>
                  </Col>
                </Row>
                {(printData.address || printData.city || printData.state || printData.country || printData.pinCode) && (
                  <div className="mb-3">
                    <h5>Address of Origin</h5>
                    <p>
                      {printData.address && <>{printData.address}<br /></>}
                      {printData.city && <>{printData.city}, </>}
                      {printData.state && <>{printData.state} </>}
                      {printData.pinCode && <>{printData.pinCode}<br /></>}
                      {printData.country && <>{printData.country}</>}
                    </p>
                  </div>
                )}
                <Row className="mb-3">
                  <Col md={4}>
                    <Card>
                      <Card.Body className="text-center">
                        <h6>Gross Weight</h6>
                        <h4>
                          {parseFloat(printData.grossWeight || 0).toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 })} {uom}
                        </h4>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={4}>
                    <Card>
                      <Card.Body className="text-center">
                        <h6>Empty Weight</h6>
                        <h4>
                          {parseFloat(printData.emptyWeight || 0).toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 })} {uom}
                        </h4>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={4}>
                    <Card className="bg-primary text-white">
                      <Card.Body className="text-center">
                        <h6>Net Weight</h6>
                        <h4>
                          {parseFloat(printData.netWeight || 0).toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 })} {uom}
                        </h4>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
                {printData.remarks && (
                  <div className="mt-3">
                    <p><strong>Remarks:</strong> {printData.remarks}</p>
                  </div>
                )}
              </div>
            );
          })()}
          {printType === 'VENDOR_TYPE' && printData && (
            <div className="print-content">
              <div className="text-center mb-4">
                <h2>VENDOR TYPE</h2>
                <p className="text-muted"><strong>{printData.name}</strong></p>
              </div>
              <Row>
                <Col md={6}>
                  <p><strong>Name:</strong> {printData.name || '-'}</p>
                </Col>
                <Col md={6}>
                  <p><strong>Status:</strong> {printData.isActive ? 'Active' : 'Inactive'}</p>
                </Col>
              </Row>
            </div>
          )}
          {printType === 'VENDOR' && printData && (
            <div className="print-content">
              <div className="text-center mb-4">
                <h2>VENDOR</h2>
                <p className="text-muted"><strong>{printData.name}</strong></p>
              </div>
              <Row className="mb-3">
                <Col md={6}>
                  <p><strong>Name:</strong> {printData.name || '-'}</p>
                  <p><strong>Company:</strong> {printData.companyName || '-'}</p>
                  <p><strong>Contact Person:</strong> {printData.contactPerson || '-'}</p>
                  <p><strong>Email:</strong> {printData.email || '-'}</p>
                </Col>
                <Col md={6}>
                  <p><strong>Phone:</strong> {printData.phoneNumber || '-'}</p>
                  <p><strong>Vendor Type:</strong> {printData.vendorType?.name || '-'}</p>
                  <p><strong>Status:</strong> {printData.isActive ? 'Active' : 'Inactive'}</p>
                  {(printData.address || printData.city || printData.state || printData.country) && (
                    <p><strong>Address:</strong> {[printData.address, printData.city, printData.state, printData.country].filter(Boolean).join(', ')}</p>
                  )}
                </Col>
              </Row>
            </div>
          )}
          {printType === 'VENDOR_PAYMENT' && printData && (
            <div className="print-content">
              <div className="text-center mb-4">
                <h2>VENDOR PAYMENT</h2>
                <p className="text-muted">Payment Number: <strong>{printData.paymentNumber}</strong></p>
              </div>
              <Row className="mb-3">
                <Col md={6}>
                  <p><strong>Payment Date:</strong> {formatDate(printData.paymentDate)}</p>
                  <p><strong>Vendor:</strong> {printData.vendor?.name || '-'}</p>
                  <p><strong>Amount:</strong> {formatCurrency(printData.amount)}</p>
                  <p><strong>Payment Method:</strong> {printData.paymentMethod || '-'}</p>
                </Col>
                <Col md={6}>
                  <p><strong>Source Account:</strong> {printData.sourceAccount?.name || '-'}</p>
                  <p><strong>Payment Type:</strong> {printData.paymentType || '-'}</p>
                  <p><strong>Status:</strong> {printData.status || '-'}</p>
                  {printData.referenceNumber && (
                    <p><strong>Reference Number:</strong> {printData.referenceNumber}</p>
                  )}
                </Col>
              </Row>
              {printData.paymentApplications && printData.paymentApplications.length > 0 && (
                <Table striped bordered className="mt-3">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Purchase Order</th>
                      <th>Applied Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {printData.paymentApplications.map((app, idx) => (
                      <tr key={app.id || idx}>
                        <td>{idx + 1}</td>
                        <td>{app.purchaseOrder?.poNumber || '-'}</td>
                        <td>{formatCurrency(app.appliedAmount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
              {printData.notes && (
                <div className="mt-3">
                  <p><strong>Notes:</strong> {printData.notes}</p>
                </div>
              )}
            </div>
          )}
                  </OrgPrintChrome>
        </Modal.Body>
      </Modal>

      {/* Help Pane */}
      {helpContent && (
        <HelpPane
          show={showHelpPane}
          onHide={() => setShowHelpPane(false)}
          title={helpContent.title}
        >
          {helpContent.content}
        </HelpPane>
      )}

    </Container>
  );
}

export default InventoryManagement;

